import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { Container, Row, Col, Card, Tab, Nav, Form, Button, Modal } from 'react-bootstrap';
import {
  FaWarehouse, FaHome, FaBuilding, FaBook, FaUser,
  FaEdit, FaPhone, FaEnvelope, FaUserTie, FaFilePdf,
  FaVideo, FaFileAlt, FaTimes, FaProjectDiagram,
  FaBell, FaCalendarAlt, FaListAlt, FaBug, FaComments,
  FaEye, FaEyeSlash, FaDownload, FaLock, FaCheckCircle, FaExclamationTriangle,
  FaUpload, FaSave, FaSpinner, FaClock, FaTimesCircle,
  FaFileWord, FaTag, FaExternalLinkAlt, FaRegMoneyBillAlt, FaInfoCircle,
  FaFutbol, FaFilePowerpoint
} from 'react-icons/fa';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, storage, db } from '../../config/firebaseConfig';
import { doc, getDoc, updateDoc, addDoc, collection, getDocs, query, where, orderBy, onSnapshot, serverTimestamp, Timestamp } from 'firebase/firestore';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { motion, AnimatePresence } from 'framer-motion';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import styles from './ProfilePage.module.css';
import PersonalCalendar from './PersonalCalendar';
import ProjectVisitsModal from './ProjectVisitsModal';
import Notifications from './Notifications';
import Agenda from './Agenda';
import { toast, Toaster } from 'react-hot-toast';
import EducationModule from './EducationModule';
import EducationPlayer from './EducationPlayer';
import ExpenseRequests from '../../components/ExpenseRequests';
import { useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel, BorderStyle, Table, TableRow, TableCell, WidthType } from 'docx';
import { saveAs } from 'file-saver';
import CVManagementModal from './CVManagementModal';
import { calculateLeaveDays, computeAnnualBalance, getAnnualEntitlement } from '../../utils/leaveBalance';
import GridStriker, { GRID_PLAYERS } from '../../components/GridStriker/GridStriker';

const Messenger = lazy(() => import('../../components/Messenger'));

const AnimatedCounter = ({ from, to, duration = 2, delay = 0 }) => {
  const [count, setCount] = useState(from);
  
  useEffect(() => {
    // Don't start animation immediately to allow staggered animations
    const timeoutId = setTimeout(() => {
      let startTime;
      const updateCount = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        const currentCount = Math.floor(from + progress * (to - from));
        
        setCount(currentCount);
        
        if (progress < 1) {
          requestAnimationFrame(updateCount);
        }
      };
      
      requestAnimationFrame(updateCount);
    }, delay * 1000);
    
    return () => clearTimeout(timeoutId);
  }, [from, to, duration, delay]);
  
  return <>{count}</>;
};

/**
 * Bir proje kartının "pasif" sayılması iki ayrı duruma bağlı:
 *   - project.status === 'passive'      → çalışanın o projedeki görevi pasif
 *     (Employees.projects[] içinde merkez/rol bazında tutulur)
 *   - project.projectStatus === 'Pasif' → projenin kendisi pasif
 *     (Projects dokümanından zenginleştirilerek gelir)
 * Filtre eskiden yalnızca ikincisine bakıyordu, bu yüzden görevi pasife
 * alınmış projeler "Pasif Projeleri Gizle" ile gizlenemiyordu.
 */
const isPassiveProject = (project) =>
  project?.status === 'passive' || project?.projectStatus === 'Pasif';

const ProfilePage = () => {
  const location = useLocation();
  const { profileImageUrl, setProfileImageUrl, employeeData, employeeId } = useUserProfile();
  const [userData, setUserData] = useState({
    eName: '',
    eSurname: '',
    role: '',
    phone: '',
    emergencyPhone: '', // Acil durum telefonu
    eMail: '',
    address: '', // Ev adresi (alan adı geriye dönük uyumluluk için 'address')
    workAddress: '',
    annualPermit: '',
    projects: []
  });
  const [error, setError] = useState(null);
  const [errorSummary, setErrorSummary] = useState('');
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [showVisitsModal, setShowVisitsModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showEducationTitles, setShowEducationTitles] = useState(false);
  const [showSOPs, setShowSOPs] = useState(true);
  const [sopList, setSOPList] = useState([]);
  const [viewedSOPs, setViewedSOPs] = useState(new Set());
  const [readSOPs, setReadSOPs] = useState(new Set()); // Okudum checkbox tracking
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false); // Track if user scrolled to bottom of SOP
  const [mainSOPList, setMainSOPList] = useState([]);
  const [showMainSOPs, setShowMainSOPs] = useState(true);
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [showSOPModal, setShowSOPModal] = useState(false);
  const [formsList, setFormsList] = useState([]);
  const [showForms, setShowForms] = useState(false);
  const [viewedForms, setViewedForms] = useState(new Set());
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'dosyalar');
  const [messengerLoaded, setMessengerLoaded] = useState(false);
  const [showEducationModule, setShowEducationModule] = useState(false);
  const [selectedEducationSOP, setSelectedEducationSOP] = useState(null);
  
  // Adres düzenleme state'leri
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState('');
  const [editingWorkAddress, setEditingWorkAddress] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  
  // Telefon düzenleme state'leri
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [editingEmergencyPhone, setEditingEmergencyPhone] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  
  // Indication Education (Endikasyon Eğitim) state'leri
  const [indicationEducations, setIndicationEducations] = useState([]);
  const [showIndicationEducation, setShowIndicationEducation] = useState(false);
  const [selectedIndicationEducation, setSelectedIndicationEducation] = useState(null);
  const [showEducationPlayer, setShowEducationPlayer] = useState(false);
  const [viewedIndicationEducations, setViewedIndicationEducations] = useState(new Set());
  
  // Search state'leri
  const [sopSearchTerm, setSOPSearchTerm] = useState('');
  const [formsSearchTerm, setFormsSearchTerm] = useState('');

  // Form adları hem kodu hem başlığı içeriyor ("MT.FR.01 Masraf Elden Teslim
  // Formu"), bu yüzden tek alanda arama yeterli. Küçültme Türkçe yerel ayarıyla
  // yapılır: aksi hâlde "İ" → "i̇" olup "iletişim" gibi aramalar tutmuyor.
  // Aktif proje sayısı - pasif görev ve pasif proje ayrımı için tek kural
  const activeProjectCount = useMemo(
    () => (userData.projects || []).filter((p) => !isPassiveProject(p)).length,
    [userData.projects]
  );

  /**
   * Profil tamamlanma oranı.
   *
   * Eskiden bu değer tamamen sabitti (fotoğraf varsa 85, yoksa 75; eğitim
   * satırı her zaman "Kısmi" ve %65) ve hiçbir gerçek veriye bakmıyordu.
   * Artık üç bileşenden ağırlıklı olarak hesaplanıyor:
   *   - Kişisel bilgiler (%40): profildeki alanlardan kaçı dolu
   *   - Profil fotoğrafı (%20): var / yok
   *   - Eğitim tamamlama (%40): atanmış SOP ve eğitimlerden kaçı tamamlandı
   */
  const profileCompletion = useMemo(() => {
    const personalFields = [
      userData.eName,
      userData.eSurname,
      userData.role,
      userData.eMail,
      userData.phone,
      userData.emergencyPhone,
      userData.address,
    ];
    const filledCount = personalFields.filter(
      (value) => value !== null && value !== undefined && String(value).trim() !== ''
    ).length;
    const personalPercent = Math.round((filledCount / personalFields.length) * 100);

    const photoPercent = profileImageUrl ? 100 : 0;

    const assignedSopCount = mainSOPList.length;
    const assignedEducationCount = indicationEducations.length;
    const totalAssigned = assignedSopCount + assignedEducationCount;

    const completedSops = mainSOPList.filter((sop) => readSOPs.has(sop.id)).length;
    const completedEducations = indicationEducations.filter((edu) =>
      viewedIndicationEducations.has(edu.id)
    ).length;
    const completedAssigned = completedSops + completedEducations;

    // Atanmış içerik yoksa bu bileşen tamamlanmış sayılır; aksi hâlde
    // hiç ataması olmayan bir kullanıcı hiçbir zaman %100'e ulaşamazdı.
    const educationPercent =
      totalAssigned === 0 ? 100 : Math.round((completedAssigned / totalAssigned) * 100);

    const overall = Math.round(
      personalPercent * 0.4 + photoPercent * 0.2 + educationPercent * 0.4
    );

    const label = (percent) => {
      if (percent >= 100) return 'Tamamlandı';
      if (percent === 0) return 'Eksik';
      return 'Kısmi';
    };

    return {
      overall,
      personal: {
        percent: personalPercent,
        label: label(personalPercent),
        detail: `${filledCount}/${personalFields.length} alan dolu`,
      },
      photo: {
        percent: photoPercent,
        label: photoPercent === 100 ? 'Tamamlandı' : 'Eksik',
      },
      education: {
        percent: educationPercent,
        label: totalAssigned === 0 ? 'Atama yok' : label(educationPercent),
        detail:
          totalAssigned === 0
            ? 'Atanmış SOP/eğitim yok'
            : `${completedAssigned}/${totalAssigned} tamamlandı`,
      },
    };
  }, [
    userData.eName,
    userData.eSurname,
    userData.role,
    userData.eMail,
    userData.phone,
    userData.emergencyPhone,
    userData.address,
    profileImageUrl,
    mainSOPList,
    readSOPs,
    indicationEducations,
    viewedIndicationEducations,
  ]);

  const filteredFormsList = useMemo(() => {
    const term = formsSearchTerm.trim().toLocaleLowerCase('tr');
    if (!term) return formsList;
    return formsList.filter((form) =>
      `${form.name || ''} ${form.versionNumber || ''}`.toLocaleLowerCase('tr').includes(term)
    );
  }, [formsList, formsSearchTerm]);
  const [educationSearchTerm, setEducationSearchTerm] = useState('');
  const fileSectionRefs = useRef({});
  const [sectionToReveal, setSectionToReveal] = useState(null);
  
  // İzin talep formu state'leri
  const [selectedLeaveType, setSelectedLeaveType] = useState('yillik');
  const [leaveStartDate, setLeaveStartDate] = useState('');
  const [leaveEndDate, setLeaveEndDate] = useState('');
  const [leaveDays, setLeaveDays] = useState('0');
  const [travelDays, setTravelDays] = useState('0');
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveAddress, setLeaveAddress] = useState('');
  const [leaveReportFile, setLeaveReportFile] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  
  // İzin talepleri geçmişi state'leri
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [leaveRequestsLoading, setLeaveRequestsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(3); // Her sayfada 3 izin talebi
  
  // Pasif projeleri göster/gizle state'i
  const [showPassiveProjects, setShowPassiveProjects] = useState(true);

  // Masraf talep formu state'leri
  const [expenseSubmitted, setExpenseSubmitted] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('Diğer');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expensePdfFiles, setExpensePdfFiles] = useState([]);
  const [expensePdfUploading, setExpensePdfUploading] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [expenseErrors, setExpenseErrors] = useState({});

  // Masraf takip state'leri
  const [myExpenseRequests, setMyExpenseRequests] = useState([]);
  const [expenseTrackingLoading, setExpenseTrackingLoading] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');

  // Masraf düzenleme state'leri
  const [showEditExpenseModal, setShowEditExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editExpenseAmount, setEditExpenseAmount] = useState('');
  const [editExpenseCategory, setEditExpenseCategory] = useState('');
  const [editExpenseDescription, setEditExpenseDescription] = useState('');
  const [editExpenseLoading, setEditExpenseLoading] = useState(false);
  const [editExpensePdfFiles, setEditExpensePdfFiles] = useState([]);
  const [editExpensePdfUploading, setEditExpensePdfUploading] = useState(false);
  const [keepExistingPdf, setKeepExistingPdf] = useState(true);

  // Masraf kategorileri
  const expenseCategories = [
    'Ulaşım',
    'Yemek',
    'Konaklama',
    'Malzeme',
    'Eğitim',
    'Diğer'
  ];

  // Proje düzenleme state'leri
  const [showProjectEditModal, setShowProjectEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingProjectIndex, setEditingProjectIndex] = useState(null);
  const [projectEditWorkerType, setProjectEditWorkerType] = useState('');
  const [projectEditBlindStatus, setProjectEditBlindStatus] = useState('');
  const [projectEditLoading, setProjectEditLoading] = useState(false);

  // Gruplandırılmış tab state'leri
  const [activeTabGroup, setActiveTabGroup] = useState('dosyalar');
  const [activeSubTab, setActiveSubTab] = useState('');

  // Dilekçe formu state'leri
  const [complaintSubject, setComplaintSubject] = useState('');
  const [complaintDetails, setComplaintDetails] = useState('');
  const [complaintType, setComplaintType] = useState('');
  const [myComplaints, setMyComplaints] = useState([]);
  const [complaintLoading, setComplaintLoading] = useState(false);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState([]);

  // Dilekçe türleri
  const complaintTypes = [
    { value: 'sikayet', label: 'Şikayet Dilekçesi' },
    { value: 'talep', label: 'Talep Dilekçesi' },
    { value: 'itiraz', label: 'İtiraz Dilekçesi' },
    { value: 'mobbing', label: 'Mobbing Bildirimi' },
    { value: 'cinsel_taciz', label: 'Cinsel Taciz Bildirimi' },
    { value: 'sozlu_taciz', label: 'Sözlü Taciz Bildirimi' },
    { value: 'fiziksel_taciz', label: 'Fiziksel Taciz Bildirimi' },
    { value: 'psikolojik_siddet', label: 'Psikolojik Şiddet Bildirimi' },
    { value: 'ayrimcilik', label: 'Ayrımcılık Bildirimi' },
    { value: 'is_guvenligi', label: 'İş Güvenliği İhlali Bildirimi' },
    { value: 'etik_ihlali', label: 'Etik İhlal Bildirimi' },
    { value: 'oneri', label: 'Öneri Dilekçesi' },
    { value: 'diger', label: 'Diğer' }
  ];

  // Özgeçmiş yönetimi state'leri - Modal içinde yönetiliyor
  const [showCVModal, setShowCVModal] = useState(false);

  // Dilekçe numarası oluştur
  const generateDocumentNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `DLK-${year}${month}${day}-${random}`;
  };

  // Bugünün tarihini formatla
  const formatDate = (date) => {
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    const d = new Date(date);
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  // Dilekçe bildirilebilecek kişiler - dinamik olarak hesaplanır
  const getComplaintTargets = () => {
    const baseTargets = [
      { email: 'hatice.alev@habcare.com.tr', name: 'Hatice Alev' },
      { email: 'arife.sahin@habcare.com.tr', name: 'Arife Şahin' }
    ];

    // Admin test kullanıcısı için kendine gönderim seçeneği ekle
    const currentUserEmail = auth.currentUser?.email?.toLowerCase();
    if (currentUserEmail === 'turanemre1999@gmail.com') {
      return [
        { email: 'turanemre1999@gmail.com', name: 'Kendime Gönder (Test)', isTest: true },
        ...baseTargets
      ];
    }

    return baseTargets;
  };

  const complaintTargets = getComplaintTargets();

  // Target seçimini toggle et
  const toggleTarget = (email) => {
    setSelectedTargets(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  // Saha Satrancı oyunu - sadece izinli kullanıcılar (GRID_PLAYERS) görür.
  // Gizli: Dosyalar sekmesine arka arkaya 3 kez tıklanınca açılır.
  const [isGamePlayer, setIsGamePlayer] = useState(
    GRID_PLAYERS.includes(auth.currentUser?.email?.toLowerCase())
  );
  const [gameUnlocked, setGameUnlocked] = useState(false);
  const gameClickCountRef = useRef(0);

  // Admin Şikayet Yönetimi state'leri
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isComplaintTarget, setIsComplaintTarget] = useState(false);
  const [allComplaints, setAllComplaints] = useState([]);
  const [allComplaintsLoading, setAllComplaintsLoading] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showComplaintStatusModal, setShowComplaintStatusModal] = useState(false);
  const [complaintStatusLoading, setComplaintStatusLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  // Şikayet durum seçenekleri
  const complaintStatusOptions = [
    { value: 'PENDING', label: 'Beklemede' },
    { value: 'INCELENIYOR', label: 'İnceleniyor' },
    { value: 'RESOLVED', label: 'Çözüldü' },
    { value: 'CLOSED', label: 'Kapatıldı' }
  ];

  // Opened file section is revealed immediately after it renders
  useEffect(() => {
    if (!sectionToReveal) return undefined;

    const sectionIsOpen = {
      sop: showMainSOPs,
      education: showEducationTitles,
      forms: showForms
    }[sectionToReveal];

    if (!sectionIsOpen) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      const sectionNode = fileSectionRefs.current[sectionToReveal];

      if (sectionNode) {
        sectionNode.scrollIntoView({ behavior: 'smooth', block: 'start' });

        try {
          sectionNode.focus({ preventScroll: true });
        } catch {
          sectionNode.focus();
        }
      }

      setSectionToReveal(null);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [sectionToReveal, showMainSOPs, showEducationTitles, showForms]);

  // Admin rolünü ve şikayet hedefi olup olmadığını kontrol et
  useEffect(() => {
    const targetEmails = complaintTargets.map(t => t.email.toLowerCase());

    const checkUserRole = async (user) => {
      if (user) {
        // Şikayet hedefi olup olmadığını kontrol et
        if (targetEmails.includes(user.email.toLowerCase())) {
          setIsComplaintTarget(true);
        }

        try {
          // Run all 3 admin checks in parallel instead of sequentially
          const usersRef = collection(db, 'Users');
          const [userDocSnap, emailSnapshot, eMailSnapshot] = await Promise.all([
            getDoc(doc(db, 'Users', user.uid)),
            getDocs(query(usersRef, where('email', '==', user.email))),
            getDocs(query(usersRef, where('eMail', '==', user.email)))
          ]);

          if (
            (userDocSnap.exists() && userDocSnap.data().role === 'admin') ||
            (!emailSnapshot.empty && emailSnapshot.docs[0].data().role === 'admin') ||
            (!eMailSnapshot.empty && eMailSnapshot.docs[0].data().role === 'admin')
          ) {
            setIsUserAdmin(true);
          }
        } catch (error) {
          console.error("Error checking user role:", error);
        }
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsGamePlayer(GRID_PLAYERS.includes(user?.email?.toLowerCase()));
      if (user) {
        checkUserRole(user);
      } else {
        setIsUserAdmin(false);
        setIsComplaintTarget(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Gelen şikayetleri getir (Admin ve şikayet hedefleri için)
  const fetchAllComplaints = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setAllComplaintsLoading(true);
    try {
      const q = query(collection(db, 'Complaints'));
      const querySnapshot = await getDocs(q);
      let complaints = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Admin tüm şikayetleri görebilir, şikayet hedefleri sadece kendilerine gönderilenleri
      if (!isUserAdmin && isComplaintTarget) {
        complaints = complaints.filter(complaint =>
          complaint.targetEmails?.some(email =>
            email.toLowerCase() === user.email.toLowerCase()
          )
        );
      }

      // Client-side sorting
      complaints.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });

      setAllComplaints(complaints);
    } catch (error) {
      console.error("Error fetching all complaints: ", error);
      toast.error('Gelen şikayetler yüklenirken bir hata oluştu.');
    } finally {
      setAllComplaintsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'gelen-sikayetler' && (isUserAdmin || isComplaintTarget)) {
      fetchAllComplaints();
    }
  }, [activeTab, isUserAdmin, isComplaintTarget]);

  // Şikayet durumunu güncelle
  const handleComplaintStatusUpdate = async (newStatus) => {
    if (!selectedComplaint) return;

    setComplaintStatusLoading(true);
    try {
      const complaintRef = doc(db, 'Complaints', selectedComplaint.id);
      await updateDoc(complaintRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      // Local state güncelle
      setAllComplaints(prev =>
        prev.map(c =>
          c.id === selectedComplaint.id ? { ...c, status: newStatus } : c
        )
      );

      toast.success('Şikayet durumu başarıyla güncellendi.');
      setShowComplaintStatusModal(false);
      setSelectedComplaint(null);
    } catch (error) {
      console.error("Error updating complaint status: ", error);
      toast.error('Şikayet durumu güncellenirken bir hata oluştu.');
    } finally {
      setComplaintStatusLoading(false);
    }
  };

  // Şikayete tıklandığında modal aç
  const handleComplaintClick = (complaint) => {
    setSelectedComplaint(complaint);
    setReplyText(''); // Clear reply text when opening modal
    setShowComplaintStatusModal(true);
  };

  // Dilekçe yanıtı gönder
  const handleReplySubmit = async () => {
    if (!selectedComplaint || !replyText.trim()) {
      toast.error('Lütfen bir yanıt yazınız.');
      return;
    }

    setReplyLoading(true);
    try {
      const user = auth.currentUser;
      const replierName = userData.eName && userData.eSurname
        ? `${userData.eName} ${userData.eSurname}`
        : (user?.displayName || user?.email || 'Yetkili');

      const newReply = {
        text: replyText.trim(),
        repliedBy: replierName,
        repliedByEmail: user?.email || '',
        repliedAt: new Date()
      };

      // Get existing replies or create new array
      const existingReplies = selectedComplaint.replies || [];
      const updatedReplies = [...existingReplies, newReply];

      const complaintRef = doc(db, 'Complaints', selectedComplaint.id);
      await updateDoc(complaintRef, {
        replies: updatedReplies,
        lastReplyAt: new Date()
      });

      // Local state güncelle
      setAllComplaints(prev =>
        prev.map(c =>
          c.id === selectedComplaint.id
            ? { ...c, replies: updatedReplies, lastReplyAt: new Date() }
            : c
        )
      );

      // Update selected complaint for modal
      setSelectedComplaint(prev => ({
        ...prev,
        replies: updatedReplies,
        lastReplyAt: new Date()
      }));

      toast.success('Yanıtınız başarıyla gönderildi.');
      setReplyText('');
    } catch (error) {
      console.error("Error submitting reply: ", error);
      toast.error('Yanıt gönderilirken bir hata oluştu.');
    } finally {
      setReplyLoading(false);
    }
  };

  // İzin türü çevirisi
  const leaveTypeTranslations = {
    'yillik': 'Yıllık İzin',
    'mazeret': 'Mazeret İzni',
    'raporlu': 'Raporlu',
    'dogum_oncesi': 'Doğum Öncesi İzin',
    'babalik': 'Babalık İzni',
    'evlilik': 'Evlilik İzni',
    'olum': 'Ölüm İzni',
    'engelli_cocuk': 'Engelli Çocuk Mazeret İzni',
    'dogum_sonrasi': 'Doğum Sonrası İzin',
    'yatakli_tedavi': 'Yataklı Kurumda Tedavi',
    'refakat': 'Refakat İzni',
    'sua': 'Şua İzni',
    'saglik_kurulu': 'Sağlık Kurulu Raporu',
    'ayakta_tedavi': 'Ayakta Tedavi'
  };

  useEffect(() => {
    let unsubscribeUserData = null;

    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          // Fetch profile image
          const imageRef = ref(storage, `profileImages/${user.uid}.jpg`);
          try {
            const url = await getDownloadURL(imageRef);
            setProfileImageUrl(url);
          } catch (error) {
            if (error.code === 'storage/object-not-found') {
              console.log("Profile image not found, skipping.");
            } else {
              throw error;
            }
          }
  
          // Fetch employee data from Firestore with real-time listener
          const employeesRef = collection(db, 'Employees');
          const q = query(employeesRef, where('eMail', '==', user.email));
          
          // Set up real-time listener
          unsubscribeUserData = onSnapshot(q, async (querySnapshot) => {
            if (!querySnapshot.empty) {
              const data = querySnapshot.docs[0].data();
              
              // Fetch project status from Projects collection
              let enrichedProjects = data.projects || [];
              if (enrichedProjects.length > 0) {
                try {
                  // Get all unique project IDs
                  const projectIds = [...new Set(enrichedProjects.map(p => p.id).filter(Boolean))];
                  
                  if (projectIds.length > 0) {
                    // Fetch projects from Projects collection
                    const projectsRef = collection(db, 'Projects');
                    const projectsSnapshot = await getDocs(projectsRef);
                    
                    // Create a map of projectId -> projectStatus
                    const projectStatusMap = {};
                    projectsSnapshot.docs.forEach(doc => {
                      const projectData = doc.data();
                      projectStatusMap[doc.id] = projectData.projectStatus || 'Aktif';
                    });
                    
                    // Enrich employee projects with projectStatus
                    enrichedProjects = enrichedProjects.map(project => ({
                      ...project,
                      projectStatus: project.id ? projectStatusMap[project.id] : 'Aktif'
                    }));
                  }
                } catch (error) {
                  console.error('Error fetching project status:', error);
                }
              }
              
              setUserData({
                eName: data.eName || '',
                eSurname: data.eSurname || '',
                role: data.role || '',
                phone: data.phone || '',
                emergencyPhone: data.emergencyPhone || '', // Acil durum telefonu
                eMail: data.eMail || '',
                address: data.address || '',
                workAddress: data.workAddress || '',
                annualPermit: data.annualPermit ?? '',
                projects: enrichedProjects
              });
              console.log('🔄 User data updated (real-time):', {
                projectCount: enrichedProjects.length,
                projects: enrichedProjects.map(p => ({
                  title: p.titleShort,
                  workerType: p.workerType,
                  blindStatus: p.blindStatus,
                  projectStatus: p.projectStatus
                }))
              });
            } else {
              console.log("No such employee!");
            }
          }, (error) => {
            console.error("Error in real-time listener: ", error);
            setError("Failed to load user data");
          });
        } catch (error) {
          console.error("Error fetching user data: ", error);
          setError("Failed to load user data");
        }
      }
    };
  
    const fetchEmployeesFromFirestore = async () => {
      try {
        const user = auth.currentUser;
        const employeesRef = collection(db, 'Employees');
        const querySnapshot = await getDocs(employeesRef);

        // OPTIMIZATION: First render employees with Firestore profile URLs only (fast)
        // This avoids blocking the UI with 100+ Firebase Storage calls
        const employeesData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            // Use profileImageUrl from Firestore directly (no storage lookup)
            profileImageUrl: data.profileImageUrl || null
          };
        });

        // Filter and sort
        const filteredEmployees = employeesData
          .filter(empData => empData.eMail !== user.email && empData.Active === 1)
          .sort((a, b) => {
            const nameA = (a.eName || '').toLowerCase();
            const nameB = (b.eName || '').toLowerCase();
            return nameA.localeCompare(nameB, 'tr');
          });

        setUsers(filteredEmployees);

        // OPTIMIZATION: Load missing profile images asynchronously in background
        // Accumulate ALL results, then do a single state update to avoid cascading re-renders
        const employeesWithoutImages = filteredEmployees.filter(emp => !emp.profileImageUrl && emp.eMail);
        
        if (employeesWithoutImages.length > 0) {
          const allImageResults = [];
          
          // Load images in batches to avoid overwhelming the network
          const batchSize = 5;
          for (let i = 0; i < employeesWithoutImages.length; i += batchSize) {
            const batch = employeesWithoutImages.slice(i, i + batchSize);
            
            const imagePromises = batch.map(async (emp) => {
              const paths = [
                `profileImages/${emp.id}.jpg`,
                `profileImages/${emp.eMail}.jpg`,
              ];

              for (const path of paths) {
                try {
                  const imageRef = ref(storage, path);
                  const url = await getDownloadURL(imageRef);
                  return { id: emp.id, profileImageUrl: url };
                } catch (err) {
                  // Continue to next path
                }
              }
              return null;
            });

            const results = await Promise.all(imagePromises);
            const validResults = results.filter(r => r !== null);
            allImageResults.push(...validResults);
          }
          
          // Single state update with all loaded images (avoids 20+ re-renders)
          if (allImageResults.length > 0) {
            setUsers(prev => prev.map(emp => {
              const found = allImageResults.find(r => r.id === emp.id);
              return found ? { ...emp, profileImageUrl: found.profileImageUrl } : emp;
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching employees: ', error);
      }
    };
  
    fetchUserData();
    fetchEmployeesFromFirestore();

    // Cleanup function
    return () => {
      if (unsubscribeUserData) {
        unsubscribeUserData();
      }
    };
  }, [setProfileImageUrl]);

  // Track unread messages count - optimized with server-side filtering
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Filter on server-side instead of fetching ALL messages
    const unreadMessagesQuery = query(
      collection(db, 'messages'),
      where('receiverEmail', '==', user.email),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(unreadMessagesQuery, (snapshot) => {
      setTotalUnreadMessages(snapshot.size);
    });

    return () => unsubscribe();
  }, []);

  // İzin talepleri sekmesi açıldığında verileri yükle

  // Adres güncelleme fonksiyonu
  const handleAddressUpdate = async () => {
    // Ev adresi zorunlu, iş adresi opsiyonel
    if (!editingAddress.trim()) {
      toast.error('⚠️ Ev adresi boş olamaz');
      return;
    }

    setAddressLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('❌ Kullanıcı oturum bilgisi bulunamadı');
        return;
      }

      // Firestore'da kullanıcının belgesini bul ve güncelle
      const employeesRef = collection(db, 'Employees');
      const q = query(employeesRef, where('eMail', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = doc(db, 'Employees', querySnapshot.docs[0].id);
        await updateDoc(docRef, {
          address: editingAddress.trim(),
          workAddress: editingWorkAddress.trim()
        });

        toast.success('✅ Adres bilgileri başarıyla güncellendi');
        setShowAddressModal(false);
      } else {
        toast.error('❌ Kullanıcı bilgisi bulunamadı');
      }
    } catch (error) {
      console.error('Adres güncelleme hatası:', error);
      toast.error('❌ Adres güncellenirken bir hata oluştu');
    } finally {
      setAddressLoading(false);
    }
  };

  // Adres düzenleme modal'ını aç
  const handleOpenAddressModal = () => {
    setEditingAddress(userData.address || '');
    setEditingWorkAddress(userData.workAddress || '');
    setShowAddressModal(true);
  };
  
  // Telefon güncelleme fonksiyonu - Sadece acil durum telefonu
  const handlePhoneUpdate = async () => {
    setPhoneLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('❌ Kullanıcı oturum bilgisi bulunamadı');
        return;
      }

      // Firestore'da kullanıcının belgesini bul ve güncelle
      const employeesRef = collection(db, 'Employees');
      const q = query(employeesRef, where('eMail', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = doc(db, 'Employees', querySnapshot.docs[0].id);
        await updateDoc(docRef, {
          emergencyPhone: editingEmergencyPhone.trim() // Sadece acil durum telefonu
        });

        toast.success('✅ Acil durum telefonu başarıyla güncellendi');
        setShowPhoneModal(false);
        
        // Local state'i de güncelle
        setUserData(prev => ({
          ...prev,
          emergencyPhone: editingEmergencyPhone.trim()
        }));
      } else {
        toast.error('❌ Kullanıcı bilgisi bulunamadı');
      }
    } catch (error) {
      console.error('Telefon güncelleme hatası:', error);
      toast.error('❌ Acil durum telefonu güncellenirken bir hata oluştu');
    } finally {
      setPhoneLoading(false);
    }
  };

  // Telefon düzenleme modal'ını aç - Sadece acil durum telefonu
  const handleOpenPhoneModal = () => {
    setEditingEmergencyPhone(userData.emergencyPhone || '');
    setShowPhoneModal(true);
  };
  
  useEffect(() => {
    if (activeTab === 'izin-taleplerim') {
      fetchMyLeaveRequests();
      setCurrentPage(1); // Sekme değiştiğinde ilk sayfaya dön
    }
    // İzin Talep formundaki bakiye kartı için de geçmişi yükle
    if (activeTab === 'izin-talep') {
      fetchMyLeaveRequests();
    }
  }, [activeTab]);

  // Tarihlere göre izin gününü otomatik hesapla (Cmt 0.5 / Paz 0 / hafta içi 1)
  useEffect(() => {
    if (leaveStartDate && leaveEndDate) {
      setLeaveDays(String(calculateLeaveDays(leaveStartDate, leaveEndDate)));
    }
  }, [leaveStartDate, leaveEndDate]);

  // Yıllık izin bakiyesi (talep formunda gösterilir).
  const annualLeaveBalance = useMemo(() => computeAnnualBalance({
    entitlement: getAnnualEntitlement(userData),
    leaveRequests: myLeaveRequests,
    employeeLeaves: [],
    year: new Date().getFullYear(),
  }), [userData, myLeaveRequests]);

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil(myLeaveRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = myLeaveRequests.slice(startIndex, endIndex);

  // Sayfa değiştirme fonksiyonları
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      scrollToTop();
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      scrollToTop();
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    scrollToTop();
  };

  // Şikayetleri getir
  const fetchMyComplaints = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setComplaintsLoading(true);
    try {
      // Firestore index hatasını önlemek için orderBy'ı kaldırdık ve client-side sıralama yapıyoruz
      const q = query(
        collection(db, 'Complaints'),
        where('uid', '==', user.uid)
      );
      const querySnapshot = await getDocs(q);
      const complaints = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Client-side sorting
      complaints.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });

      setMyComplaints(complaints);
    } catch (error) {
      console.error("Error fetching complaints: ", error);
      toast.error('Şikayet geçmişi yüklenirken bir hata oluştu.');
    } finally {
      setComplaintsLoading(false);
    }
  };

  // Şikayet sekmesi açıldığında verileri yükle
  useEffect(() => {
    if (activeTab === 'sikayetlerim') {
      fetchMyComplaints();
    }
  }, [activeTab]);

  // Dilekçe DOCX dosyası oluştur
  const generateDilekceDocument = async (data) => {
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          // Başlık - Şirket Bilgisi
          new Paragraph({
            children: [
              new TextRun({
                text: "HabcareCRO",
                bold: true,
                size: 28,
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
          }),

          // Tarih
          new Paragraph({
            children: [
              new TextRun({
                text: `Tarih: ${data.formattedDate}`,
                size: 22,
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { after: 200 },
          }),

          // Dilekçe Numarası
          new Paragraph({
            children: [
              new TextRun({
                text: `Dilekçe No: ${data.documentNumber}`,
                size: 22,
                italics: true,
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { after: 400 },
          }),

          // Ana Başlık
          new Paragraph({
            children: [
              new TextRun({
                text: "DİLEKÇE",
                bold: true,
                size: 36,
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 100 },
          }),

          // Alt Başlık
          new Paragraph({
            children: [
              new TextRun({
                text: data.typeLabel,
                size: 24,
                italics: true,
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          }),

          // Muhatap Bilgisi
          new Paragraph({
            children: [
              new TextRun({
                text: "MUHATAP:",
                bold: true,
                size: 22,
              }),
            ],
            spacing: { after: 100 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: data.targetNames.join(', '),
                size: 22,
              }),
            ],
            spacing: { after: 300 },
          }),

          // Başvuru Sahibi Bilgileri
          new Paragraph({
            children: [
              new TextRun({
                text: "BAŞVURU SAHİBİ BİLGİLERİ:",
                bold: true,
                size: 22,
              }),
            ],
            spacing: { after: 100 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Ad Soyad: ${data.employeeName}`,
                size: 22,
              }),
            ],
            spacing: { after: 50 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `E-posta: ${data.employeeEmail}`,
                size: 22,
              }),
            ],
            spacing: { after: 50 },
          }),
          ...(data.employeeTitle ? [new Paragraph({
            children: [
              new TextRun({
                text: `Unvan: ${data.employeeTitle}`,
                size: 22,
              }),
            ],
            spacing: { after: 50 },
          })] : []),
          ...(data.employeeDepartment ? [new Paragraph({
            children: [
              new TextRun({
                text: `Departman: ${data.employeeDepartment}`,
                size: 22,
              }),
            ],
            spacing: { after: 50 },
          })] : []),

          // Konu
          new Paragraph({
            children: [
              new TextRun({
                text: "",
              }),
            ],
            spacing: { after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: "KONU: ",
                bold: true,
                size: 22,
              }),
              new TextRun({
                text: data.subject,
                size: 22,
              }),
            ],
            spacing: { after: 300 },
          }),

          // Dilekçe İçeriği
          new Paragraph({
            children: [
              new TextRun({
                text: "Sayın İlgili,",
                size: 22,
                italics: true,
              }),
            ],
            spacing: { after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: data.details,
                size: 22,
              }),
            ],
            spacing: { after: 300 },
          }),

          // Kapanış
          new Paragraph({
            children: [
              new TextRun({
                text: "Gereğini bilgilerinize arz ederim.",
                size: 22,
                italics: true,
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { after: 100 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: "Saygılarımla,",
                size: 22,
                italics: true,
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { after: 200 },
          }),

          // İmza
          new Paragraph({
            children: [
              new TextRun({
                text: data.employeeName,
                bold: true,
                size: 24,
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { after: 100 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: data.formattedDate,
                size: 20,
              }),
            ],
            alignment: AlignmentType.RIGHT,
          }),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    return blob;
  };

  const handleComplaintSubmit = async () => {
    console.log("Submitting dilekce...");
    const user = auth.currentUser;
    if (!user) {
      console.error("No user found");
      toast.error("Oturum hatası. Lütfen sayfayı yenileyin.");
      return;
    }

    if (!complaintType) {
      toast.error('Lütfen bir dilekçe türü seçiniz.');
      return;
    }

    if (!complaintSubject || !complaintDetails) {
      toast.error('Lütfen konu ve detay alanlarını doldurunuz.');
      return;
    }

    if (selectedTargets.length === 0) {
      toast.error('Lütfen en az bir muhatap seçiniz.');
      return;
    }

    // Seçilen hedeflerin isimlerini al
    const targetNames = selectedTargets.map(email => {
      const target = complaintTargets.find(t => t.email === email);
      return target ? target.name : email;
    });

    setComplaintLoading(true);
    try {
      const documentNumber = generateDocumentNumber();
      const formattedDate = formatDate(new Date());
      const employeeName = userData.eName && userData.eSurname ? `${userData.eName} ${userData.eSurname}` : (user.displayName || user.email);
      const employeeEmail = userData.eMail || user.email;
      const typeLabel = complaintTypes.find(t => t.value === complaintType)?.label || complaintType;

      // DOCX dosyası oluştur
      const docxData = {
        documentNumber,
        formattedDate,
        employeeName,
        employeeEmail,
        employeeTitle: userData.eTitle || '',
        employeeDepartment: userData.eDepartment || '',
        subject: complaintSubject,
        details: complaintDetails,
        typeLabel,
        targetNames,
      };

      console.log("Generating DOCX document...");
      const docxBlob = await generateDilekceDocument(docxData);

      // Firebase Storage'a yükle
      const fileName = `dilekce_${documentNumber}.docx`;
      const storageRef = ref(storage, `dilekce/${user.uid}/${fileName}`);
      await uploadBytes(storageRef, docxBlob);
      const downloadUrl = await getDownloadURL(storageRef);
      console.log("DOCX uploaded to:", downloadUrl);

      const complaintData = {
        uid: user.uid,
        documentNumber: documentNumber,
        employeeName: employeeName,
        employeeEmail: employeeEmail,
        employeeTitle: userData.eTitle || '',
        employeeDepartment: userData.eDepartment || '',
        subject: complaintSubject,
        details: complaintDetails,
        type: complaintType,
        typeLabel: typeLabel,
        targetEmails: selectedTargets,
        targetNames: targetNames,
        status: 'PENDING',
        createdAt: new Date(),
        docxUrl: downloadUrl,
        docxFileName: fileName
      };

      console.log("Dilekce data:", complaintData);

      await addDoc(collection(db, 'Complaints'), complaintData);

      toast.success('Dilekçeniz başarıyla gönderildi.');
      setComplaintSubject('');
      setComplaintDetails('');
      setComplaintType('');
      setSelectedTargets([]);
      fetchMyComplaints(); // Listeyi güncelle
    } catch (error) {
      console.error("Error submitting dilekce: ", error);
      toast.error('Dilekçe gönderilirken bir hata oluştu: ' + error.message);
    } finally {
      setComplaintLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const user = auth.currentUser;
      const imageRef = ref(storage, `profileImages/${user.uid}.jpg`);

      await uploadBytes(imageRef, file);
      const newUrl = await getDownloadURL(imageRef);
      
      // Update context
      setProfileImageUrl(newUrl);

      // Update Firestore
      const employeesRef = collection(db, 'Employees');
      const q = query(employeesRef, where('eMail', '==', user.email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const employeeDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'Employees', employeeDoc.id), { 
          profileImageUrl: newUrl 
        });
      }
      
      toast.success('Profil fotoğrafı başarıyla güncellendi.');
    } catch (error) {
      console.error("Error uploading image: ", error);
      setError("Failed to upload image");
      toast.error('Profil fotoğrafı yüklenirken bir hata oluştu.');
    }
  };

  const handleErrorFeedbackSubmit = async () => {
    const user = auth.currentUser;
    if (!user || !errorSummary) {
      setError('Lütfen hata açıklamasını giriniz');
      toast.error('Lütfen hata açıklamasını giriniz');
      return;
    }

    try {
      let screenshotUrl = '';
      const timestamp = new Date().toISOString();

      // If a screenshot is uploaded, handle the file upload
      if (screenshotFile) {
        const screenshotRef = ref(storage, `errorScreenshots/${user.uid}-${timestamp}.jpg`);
        await uploadBytes(screenshotRef, screenshotFile);
        screenshotUrl = await getDownloadURL(screenshotRef);
      }

      // Get user name from userData (already fetched in component)
      let userName = 'Bilinmeyen Kullanıcı';
      if (userData) {
        const firstName = userData.firstName || userData.name || userData.eName || '';
        const lastName = userData.lastName || userData.surname || userData.eSurname || '';
        userName = `${firstName} ${lastName}`.trim();
        
        // If still empty, try to get from auth
        if (!userName) {
          userName = user.displayName || user.email || 'Bilinmeyen Kullanıcı';
        }
      }

      // Add a new document to the ErrorFeedbacks collection
      await addDoc(collection(db, 'ErrorFeedbacks'), {
        errorSummary,
        screenshotUrl,
        timestamp,
        uid: user.uid,
        userName: userName // Add userName field
      });

      // Reset the form fields
      setErrorSummary('');
      setScreenshotFile(null);
      toast.success('Hata bildirimi başarıyla gönderildi.');
    } catch (error) {
      console.error("Error submitting feedback: ", error);
      setError('Failed to submit feedback');
      toast.error('Hata bildirimi gönderilemedi.');
    }
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setShowVisitsModal(true);
  };

  const fetchViewedSOPs = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const viewsRef = collection(db, 'userSOPViews');
      const q = query(viewsRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      const viewedIds = new Set();
      querySnapshot.docs.forEach(doc => {
        viewedIds.add(doc.data().sopId);
      });
      
      setViewedSOPs(viewedIds);
    } catch (error) {
      console.error("Error fetching viewed SOPs:", error);
    }
  };

  const fetchViewedForms = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const viewsRef = collection(db, 'userFormViews');
      const q = query(viewsRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      const viewedIds = new Set();
      querySnapshot.docs.forEach(doc => {
        viewedIds.add(doc.data().formId);
      });
      
      setViewedForms(viewedIds);
    } catch (error) {
      console.error("Error fetching viewed Forms:", error);
    }
  };

  // Toggle SOP visibility — data already loaded on mount
  const handleSOPClick = () => {
    setSOPList(mainSOPList);
    setShowSOPs(true);
    setShowEducationTitles(true);
  };

  const markSOPAsViewed = async (sopId) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const viewsRef = collection(db, 'userSOPViews');
      await addDoc(viewsRef, {
        userId: user.uid,
        sopId: sopId,
        viewedAt: new Date()
      });

      setViewedSOPs(prev => new Set([...prev, sopId]));
    } catch (error) {
      console.error("Error marking SOP as viewed:", error);
    }
  };

  const markFormAsViewed = async (formId) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const viewsRef = collection(db, 'userFormViews');
      await addDoc(viewsRef, {
        userId: user.uid,
        formId: formId,
        viewedAt: new Date()
      });

      setViewedForms(prev => new Set([...prev, formId]));
    } catch (error) {
      console.error("Error marking Form as viewed:", error);
    }
  };

  // Fetch read SOPs (Okudum checkbox)
  const fetchReadSOPs = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const readRef = collection(db, 'userSOPReadConfirmations');
      const q = query(readRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);

      const readIds = new Set();
      querySnapshot.docs.forEach(doc => {
        readIds.add(doc.data().sopId);
      });

      setReadSOPs(readIds);
    } catch (error) {
      console.error("Error fetching read SOPs:", error);
    }
  };

  // Toggle SOP read status (Okudum checkbox)
  const toggleSOPRead = async (sopId) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const readRef = collection(db, 'userSOPReadConfirmations');

      if (readSOPs.has(sopId)) {
        // Remove read confirmation
        const q = query(readRef, where('userId', '==', user.uid), where('sopId', '==', sopId));
        const querySnapshot = await getDocs(q);

        for (const docSnap of querySnapshot.docs) {
          await updateDoc(doc(db, 'userSOPReadConfirmations', docSnap.id), {
            isRead: false,
            unreadAt: new Date()
          });
        }

        setReadSOPs(prev => {
          const newSet = new Set(prev);
          newSet.delete(sopId);
          return newSet;
        });
        toast.info('SOP okunmadı olarak işaretlendi');
      } else {
        // Add read confirmation
        await addDoc(readRef, {
          userId: user.uid,
          sopId: sopId,
          isRead: true,
          readAt: new Date()
        });

        setReadSOPs(prev => new Set([...prev, sopId]));
        toast.success('SOP okundu olarak işaretlendi');
      }
    } catch (error) {
      console.error("Error toggling SOP read status:", error);
      toast.error('İşlem sırasında bir hata oluştu');
    }
  };

  // Handle scroll in SOP modal to detect if user reached bottom
  const handleSOPScroll = (e) => {
    const element = e.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Check if scrolled to bottom (with a 20px threshold)
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  };

  // Toggle SOP section visibility — data already loaded on mount
  const handleFileSectionToggle = (sectionKey) => {
    const isOpen = (
      (sectionKey === 'sop' && showMainSOPs) ||
      (sectionKey === 'education' && showEducationTitles) ||
      (sectionKey === 'forms' && showForms)
    );
    const shouldOpen = !isOpen;

    setShowMainSOPs(sectionKey === 'sop' ? shouldOpen : false);
    setShowEducationTitles(sectionKey === 'education' ? shouldOpen : false);
    setShowForms(sectionKey === 'forms' ? shouldOpen : false);
    setSectionToReveal(shouldOpen ? sectionKey : null);

    // Bölüm kapanınca arama sıfırlansın; yeniden açıldığında filtrelenmiş bir
    // listeyle karşılaşmak "form kayboldu" izlenimi veriyor.
    if (!(sectionKey === 'forms' && shouldOpen)) setFormsSearchTerm('');
  };

  const handleFileSectionKeyDown = (event, sectionKey) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleFileSectionToggle(sectionKey);
    }
  };

  const handleMainSOPCardClick = () => {
    handleFileSectionToggle('sop');
  };

  const handleSOPItemClick = async (sop) => {
    // Yeniyse görüntülenmiş olarak işaretle
    if (sop.newlyAdded && !viewedSOPs.has(sop.id)) {
      markSOPAsViewed(sop.id);
    }

    // Video içeriği için Eğitim Modülünü kullan
    if (sop.fileType === 'video/mp4') {
      setSelectedEducationSOP(sop);
      setShowEducationModule(true);
    } else {
      // Diğer dosya türleri için normal modalı kullan
      setSelectedSOP(sop);
      setShowSOPModal(true);
    }
  };

  // Toggle forms visibility — data already loaded on mount
  const handleFormsCardClick = () => {
    handleFileSectionToggle('forms');
  };

  const handleFormItemClick = (form) => {
    // Yeniyse görüntülenmiş olarak işaretle
    if (form.newlyAdded && !viewedForms.has(form.id)) {
      markFormAsViewed(form.id);
    }

    if (form.fileUrl) {
      window.open(form.fileUrl, '_blank');
    } else {
      toast.info('Bu form için dosya bulunmamaktadır');
    }
  };

  const handleEducationComplete = async (sopId) => {
    // Görüntülenen SOPlara ekle
    if (!viewedSOPs.has(sopId)) {
      markSOPAsViewed(sopId);
    }
    
    // Ek tamamlama mantığı burada
    toast.success('Eğitim başarıyla tamamlandı!');
  };

  // Indication Education (Endikasyon Eğitim) functions
  const fetchViewedIndicationEducations = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const progressRef = collection(db, 'userEducationProgress');
      const q = query(progressRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      const viewedIds = new Set();
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.watchedProgress >= 50) {
          viewedIds.add(data.educationId);
        }
      });
      
      setViewedIndicationEducations(viewedIds);
    } catch (error) {
      console.error("Error fetching viewed indication educations:", error);
    }
  };

  const loadIndicationEducations = async () => {
    try {
      if (!userData.role) return;
      
      const educationsRef = collection(db, 'IndicationEducations');
      
      // Query for user's specific role AND "Hepsi" in parallel
      const [roleSnapshot, hepsiSnapshot] = await Promise.all([
        getDocs(query(educationsRef, where('group', 'array-contains', userData.role))),
        getDocs(query(educationsRef, where('group', 'array-contains', 'Hepsi')))
      ]);
      
      // Merge results and remove duplicates
      const educationsMap = new Map();
      roleSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Only include if it has a video URL (new format education)
        if (data.videoUrl) {
          educationsMap.set(doc.id, { id: doc.id, ...data });
        }
      });
      hepsiSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.videoUrl) {
          educationsMap.set(doc.id, { id: doc.id, ...data });
        }
      });
      
      setIndicationEducations(Array.from(educationsMap.values()));
    } catch (error) {
      console.error("Error loading indication educations:", error);
    }
  };

  const handleIndicationEducationClick = (education) => {
    setSelectedIndicationEducation(education);
    setShowEducationPlayer(true);
  };

  const handleIndicationEducationComplete = async (educationId) => {
    // Görüntülenen eğitimlere ekle
    setViewedIndicationEducations(prev => new Set([...prev, educationId]));
    toast.success('Eğitim başarıyla tamamlandı!');
  };

  // İzin taleplerini getir
  const fetchMyLeaveRequests = async () => {
    const currentEmployeeId = employeeId || auth.currentUser?.uid;
    if (!currentEmployeeId) {
      console.log('No current user or employee ID found');
      return;
    }
    
    console.log('Fetching leave requests for employee:', currentEmployeeId);
    setLeaveRequestsLoading(true);
    try {
      // LeaveRequests koleksiyonundan veri çek (çalışan tarafından oluşturulan)
      const q1 = query(
        collection(db, 'LeaveRequests'),
        where('employeeId', '==', currentEmployeeId)
      );
      const querySnapshot1 = await getDocs(q1);
      console.log('LeaveRequests query snapshot size:', querySnapshot1.docs.length);
      
      const leaveRequests = querySnapshot1.docs.map(doc => {
        const data = doc.data();
        console.log('Leave request data:', data);
        return {
          id: doc.id,
          ...data,
          source: 'LeaveRequests'
        };
      });

      // EmployeeLeaves koleksiyonundan veri çek (admin tarafından oluşturulan)
      const q2 = query(
        collection(db, 'EmployeeLeaves'),
        where('employeeId', '==', currentEmployeeId)
      );
      const querySnapshot2 = await getDocs(q2);
      console.log('EmployeeLeaves query snapshot size:', querySnapshot2.docs.length);
      
      const employeeLeaves = querySnapshot2.docs.map(doc => {
        const data = doc.data();
        console.log('Employee leave data:', data);
        return {
          id: doc.id,
          employeeId: data.employeeId,
          employeeName: `${userData.eName} ${userData.eSurname}`,
          employeeEmail: userData.eMail,
          leaveType: data.typeKey,
          startDate: data.startDate,
          endDate: data.workReturnDate,
          leaveDays: data.leaveDays,
          travelDays: data.travelDays,
          reason: data.reason,
          address: data.address,
          status: 'APPROVED', // Admin tarafından oluşturulan izinler otomatik onaylı
          createdAt: data.createdAt || new Date(), // Eğer createdAt yoksa şimdiki tarih
          processedAt: data.createdAt || new Date(),
          processedBy: 'Admin',
          adminNote: 'Admin tarafından oluşturuldu',
          source: 'EmployeeLeaves'
        };
      });

      // Her iki kaynaktan gelen verileri birleştir
      const allRequests = [...leaveRequests, ...employeeLeaves];
      
      // Client-side sorting by createdAt (newest first)
      const sortedRequests = allRequests.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      console.log('Final requests array:', sortedRequests);
      setMyLeaveRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      toast.error('İzin talepleri yüklenirken hata oluştu');
    } finally {
      setLeaveRequestsLoading(false);
    }
  };

  // Masraf talep formu submit fonksiyonu
  const handleExpenseRequestSubmit = async () => {
    // Inline validation
    const errors = {};
    if (!expenseAmount) {
      errors.amount = 'Tutar alanı zorunludur';
    } else if (parseFloat(expenseAmount) <= 0) {
      errors.amount = 'Tutar 0\'dan büyük olmalıdır';
    }
    if (!expenseDescription) {
      errors.description = 'Açıklama alanı zorunludur';
    }
    setExpenseErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error('⚠️ Lütfen zorunlu alanları doldurunuz');
      return;
    }

    const eName = userData.eName || employeeData?.eName || '';
    const eSurname = userData.eSurname || employeeData?.eSurname || '';
    const eMail = userData.eMail || employeeData?.eMail || auth.currentUser?.email || '';

    if (!eName || !eSurname) {
      toast.error('❌ Kullanıcı bilgileri bulunamadı');
      return;
    }

    setExpenseLoading(true);
    
    try {
      let pdfUrls = [];
      let pdfFileNames = [];
      
      // PDF dosyaları varsa yükle
      if (expensePdfFiles.length > 0) {
        setExpensePdfUploading(true);
        const timestamp = Date.now();
        
        try {
          const uploadPromises = expensePdfFiles.map(async (file, index) => {
            const fileName = `expense-${employeeId}-${timestamp}-${index + 1}.pdf`;
            const storageRef = ref(storage, `expense-documents/${fileName}`);
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);
            return { url, fileName: file.name };
          });

          const uploadResults = await Promise.all(uploadPromises);
          pdfUrls = uploadResults.map(result => result.url);
          pdfFileNames = uploadResults.map(result => result.fileName);
          
          console.log('✅ PDFs uploaded successfully:', pdfUrls);
        } catch (uploadError) {
          console.error('❌ PDF upload error:', uploadError);
          toast.error('❌ PDF yüklenirken hata oluştu');
          setExpensePdfUploading(false);
          setExpenseLoading(false);
          return;
        }
        setExpensePdfUploading(false);
      }

      const expenseRequestData = {
        employeeId: employeeId,
        employeeName: `${eName} ${eSurname}`,
        employeeEmail: eMail,
        amount: parseFloat(expenseAmount),
        category: expenseCategory,
        description: expenseDescription,
        pdfUrls: pdfUrls,
        pdfFileNames: pdfFileNames,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        requestType: 'EXPENSE_REQUEST'
      };

      const docRef = await addDoc(collection(db, 'ExpenseRequests'), expenseRequestData);
      console.log('✅ Expense request saved with ID:', docRef.id);

      // Create a task for all Finans Yöneticisi users
      try {
        // authorization may be stored as an array (CreateUserModal) or a string (EditEmployeeModal) — handle both.
        const employeesSnap = await getDocs(collection(db, 'Employees'));
        const hasFinanceRole = (auth) => {
          if (!auth) return false;
          if (Array.isArray(auth)) return auth.includes('Finans Yöneticisi');
          return auth === 'Finans Yöneticisi';
        };
        // Çalışan Yönetimi "Durum" sütunuyla birebir aynı: yalnızca Active === 1 "Aktif".
        const isActive = (data) => data.Active === 1;
        const financeAssignees = employeesSnap.docs
          .filter(d => hasFinanceRole(d.data().authorization) && isActive(d.data()))
          .map(d => {
            const data = d.data();
            const fullName = `${data.eName || ''} ${data.eSurname || ''}`.trim() || (data.eMail || 'Finans Yöneticisi');
            return { id: d.id, name: fullName };
          });

        if (financeAssignees.length > 0) {
          const requesterName = `${eName || ''} ${eSurname || ''}`.trim() || (eMail || 'Bir çalışan');
          const amountText = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(expenseAmount));
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 3);

          const taskData = {
            title: `Cevaplanması gereken bir masraf talebi bulunmakta — ${requesterName}`,
            description: `${requesterName} tarafından ${amountText} ₺ tutarında bir masraf talebi oluşturuldu.\n\nKategori: ${expenseCategory}\nAçıklama: ${expenseDescription}\n\nLütfen talebi inceleyip onaylayın veya reddedin.`,
            priority: 'medium',
            status: 'pending',
            assignees: financeAssignees,
            assigneeName: financeAssignees.map(a => a.name).join(', '),
            assigneeId: financeAssignees.length === 1 ? financeAssignees[0].id : null,
            dueDate: Timestamp.fromDate(dueDate),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: requesterName,
            source: 'EXPENSE_REQUEST',
            relatedExpenseRequestId: docRef.id,
            viewedBy: []
          };
          await addDoc(collection(db, 'Tasks'), taskData);
          console.log(`✅ Task created for ${financeAssignees.length} Finans Yöneticisi`);
        } else {
          console.warn('⚠️ No Finans Yöneticisi found — task not created');
        }
      } catch (taskError) {
        console.error('❌ Finans task creation error:', taskError);
      }

      // "Finans Yöneticisi" yetkisine sahip çalışanlara otomatik bildirim maili gönder
      try {
        const response = await fetch(
          'https://us-central1-habcare-8ecad.cloudfunctions.net/sendExpenseRequestNotification',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              employeeName: `${eName} ${eSurname}`,
              employeeEmail: eMail,
              amount: parseFloat(expenseAmount),
              category: expenseCategory,
              description: expenseDescription,
            }),
          }
        );

        if (response.ok) {
          console.log('✅ Masraf bildirim maili gönderildi (Finans Yöneticisi)');
        } else {
          console.error('❌ Masraf bildirim maili gönderilemedi:', response.status);
        }
      } catch (mailError) {
        // Mail gönderilemese bile talep kaydedildi; akışı bozma
        console.error('❌ Masraf bildirim maili hatası:', mailError);
      }

      // Form alanlarını temizle
      setExpenseAmount('');
      setExpenseCategory('Diğer');
      setExpenseDescription('');
      setExpensePdfFiles([]);
      setExpenseErrors({});
      setExpenseSubmitted(true);
      // Real-time listener otomatik olarak güncelleyecek
      
    } catch (error) {
      console.error('❌ Masraf talebi gönderme hatası:', error);
      toast.error('❌ Masraf talebi gönderilirken bir hata oluştu: ' + error.message);
    } finally {
      setExpenseLoading(false);
      setExpensePdfUploading(false);
    }
  };

  // PDF dosya seçimi (çoklu)
  const handlePdfFileSelect = (e) => {
    console.log('🔥 handlePdfFileSelect called with files:', e.target.files.length);
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    let hasError = false;

    files.forEach(file => {
      console.log('📁 Processing file:', file.name, file.type, file.size);
      if (file.type !== 'application/pdf') {
        toast.error(`❌ ${file.name} - Sadece PDF dosyaları yüklenebilir`);
        hasError = true;
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error(`❌ ${file.name} - Dosya boyutu 10MB'dan küçük olmalıdır`);
        hasError = true;
        return;
      }
      validFiles.push(file);
    });

    console.log('✅ Valid files:', validFiles.length);
    if (validFiles.length > 0) {
      setExpensePdfFiles(prev => {
        const newFiles = [...prev, ...validFiles];
        console.log('📋 Updated file list:', newFiles.map(f => f.name));
        return newFiles;
      });
      toast.success(`✅ ${validFiles.length} PDF dosyası seçildi`);
    }

    // Input'u temizle
    e.target.value = '';
  };

  // Belirli bir PDF dosyasını kaldır
  const handleRemovePdf = (index) => {
    setExpensePdfFiles(prev => prev.filter((_, i) => i !== index));
    toast.success('✅ Dosya kaldırıldı');
  };

  // Tüm PDF dosyalarını kaldır
  const handleRemoveAllPdfs = () => {
    setExpensePdfFiles([]);
    const fileInput = document.getElementById('expense-pdf-input');
    if (fileInput) fileInput.value = '';
    toast.success('✅ Tüm dosyalar kaldırıldı');
  };

  // Real-time masraf takip güncellemeleri
  useEffect(() => {
    if (!employeeId) return;

    setExpenseTrackingLoading(true);
    
    const q = query(
      collection(db, 'ExpenseRequests'),
      where('employeeId', '==', employeeId)
    );

    // Real-time listener ile otomatik güncelleme
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      results.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setMyExpenseRequests(results);
      setExpenseTrackingLoading(false);
    }, (err) => {
      console.error('Masraf talepleri yüklenemedi:', err);
      setExpenseTrackingLoading(false);
    });

    // Cleanup function
    return () => unsubscribe();
  }, [employeeId]);

  // Özgeçmişleri yükle - Modal içinde hallediliyor
  // useEffect kaldırıldı

  // Masraf güncelleme fonksiyonu
  const handleExpenseUpdate = async () => {
    if (!editingExpense) return;

    // Validation
    const errors = {};
    if (!editExpenseAmount) {
      errors.amount = 'Tutar alanı zorunludur';
    } else if (parseFloat(editExpenseAmount) <= 0) {
      errors.amount = 'Tutar 0\'dan büyük olmalıdır';
    }
    if (!editExpenseDescription) {
      errors.description = 'Açıklama alanı zorunludur';
    }

    if (Object.keys(errors).length > 0) {
      toast.error('⚠️ Lütfen zorunlu alanları doldurunuz');
      return;
    }

    setEditExpenseLoading(true);

    try {
      let pdfUrls = [];
      let pdfFileNames = [];

      // Yeni PDF dosyaları yüklenecek mi?
      if (editExpensePdfFiles.length > 0) {
        setEditExpensePdfUploading(true);
        const timestamp = Date.now();
        
        try {
          console.log('📤 Uploading PDFs...', editExpensePdfFiles.length);
          const uploadPromises = editExpensePdfFiles.map(async (file, index) => {
            const fileName = `expense-${employeeId}-${timestamp}-${index + 1}.pdf`;
            const storageRef = ref(storage, `expense-documents/${fileName}`);
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);
            return { url, fileName: file.name };
          });

          const uploadResults = await Promise.all(uploadPromises);
          pdfUrls = uploadResults.map(result => result.url);
          pdfFileNames = uploadResults.map(result => result.fileName);
          
          console.log('✅ PDFs uploaded successfully:', pdfUrls);
          setEditExpensePdfUploading(false);
        } catch (uploadError) {
          console.error('❌ PDF upload error:', uploadError);
          toast.error('❌ PDF yüklenirken hata oluştu: ' + uploadError.message);
          setEditExpensePdfUploading(false);
          setEditExpenseLoading(false);
          return;
        }
      } else if (keepExistingPdf) {
        // Mevcut PDF'leri koru
        console.log('📎 Keeping existing PDFs');
        // pdfUrls array veya pdfUrl string olabilir, her ikisini de kontrol et
        if (editingExpense.pdfUrls && Array.isArray(editingExpense.pdfUrls) && editingExpense.pdfUrls.length > 0) {
          pdfUrls = [...editingExpense.pdfUrls];
          pdfFileNames = editingExpense.pdfFileNames ? [...editingExpense.pdfFileNames] : [];
        } else if (editingExpense.pdfUrl) {
          // Eski format: tek PDF
          pdfUrls = [editingExpense.pdfUrl];
          pdfFileNames = editingExpense.pdfFileName ? [editingExpense.pdfFileName] : ['Masraf Belgesi.pdf'];
        }
        console.log('📎 Existing PDFs:', pdfUrls);
      } else {
        console.log('🗑️ No PDFs to keep');
      }

      const expenseRef = doc(db, 'ExpenseRequests', editingExpense.id);
      
      const updateData = {
        amount: parseFloat(editExpenseAmount),
        category: editExpenseCategory,
        description: editExpenseDescription,
        updatedAt: new Date().toISOString(),
      };

      // PDF güncellemesi - her zaman ekle (yeni yükleme veya mevcut koruma veya silme)
      updateData.pdfUrls = pdfUrls;
      updateData.pdfFileNames = pdfFileNames;
      // Backward compatibility için pdfUrl field'ı
      if (pdfUrls.length > 0) {
        updateData.pdfUrl = pdfUrls[0];
        updateData.pdfFileName = pdfFileNames[0] || 'Masraf Belgesi.pdf';
      } else {
        updateData.pdfUrl = null;
        updateData.pdfFileName = null;
      }

      console.log('💾 Updating expense with data:', updateData);
      await updateDoc(expenseRef, updateData);

      toast.success('✅ Masraf talebi başarıyla güncellendi');
      setShowEditExpenseModal(false);
      setEditingExpense(null);
      setEditExpensePdfFiles([]);
      setKeepExistingPdf(true);
      
      // Local state'i güncelle
      setMyExpenseRequests(prev =>
        prev.map(req =>
          req.id === editingExpense.id
            ? { ...req, ...updateData }
            : req
        )
      );
    } catch (error) {
      console.error('❌ Masraf güncelleme hatası:', error);
      toast.error('❌ Masraf güncellenirken bir hata oluştu: ' + error.message);
    } finally {
      setEditExpenseLoading(false);
      setEditExpensePdfUploading(false);
    }
  };

  // Düzenleme modalı için PDF dosyası seçme
  const handleEditExpensePdfChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    let hasError = false;

    files.forEach(file => {
      if (file.type !== 'application/pdf') {
        toast.error(`❌ ${file.name} - Sadece PDF dosyaları yüklenebilir`);
        hasError = true;
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`❌ ${file.name} - Dosya boyutu 10MB'dan küçük olmalıdır`);
        hasError = true;
        return;
      }
      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setEditExpensePdfFiles(prev => [...prev, ...validFiles]);
      setKeepExistingPdf(false); // Yeni dosya eklenince mevcut PDF'i kaldır
      toast.success(`✅ ${validFiles.length} PDF dosyası seçildi`);
    }

    e.target.value = '';
  };

  // Düzenleme modalı için PDF kaldırma
  const handleRemoveEditPdf = (index) => {
    setEditExpensePdfFiles(prev => prev.filter((_, i) => i !== index));
    toast.success('✅ Dosya kaldırıldı');
  };

  // İzin talep formu submit fonksiyonu
  const handleLeaveRequestSubmit = async () => {
    console.log('🚀 İzin talebi gönderme başladı');
    console.log('Form verileri:', {
      leaveStartDate,
      leaveEndDate,
      selectedLeaveType,
      leaveDays,
      travelDays,
      leaveReason,
      leaveAddress,
      userData: userData ? { eName: userData.eName, eSurname: userData.eSurname, eMail: userData.eMail } : 'null'
    });

    if (!leaveStartDate || !leaveEndDate) {
      console.log('❌ Tarih hatası:', { leaveStartDate, leaveEndDate });
      toast.error('⚠️ Lütfen başlangıç ve bitiş tarihlerini giriniz');
      return;
    }

    if (!userData.eName || !userData.eSurname) {
      console.log('❌ Kullanıcı bilgisi hatası:', userData);
      toast.error('❌ Kullanıcı bilgileri bulunamadı');
      return;
    }

    // Yıllık izin bakiyesi kontrolü — hak yoksa/yetmiyorsa talep engellenir.
    if (selectedLeaveType === 'yillik') {
      const requestedDays = Number(leaveDays) || 0;
      if (requestedDays <= 0) {
        toast.error('⚠️ Geçerli bir izin süresi seçiniz');
        return;
      }
      if (requestedDays > annualLeaveBalance.remaining) {
        toast.error(
          `⛔ Yetersiz yıllık izin bakiyesi. Kalan: ${annualLeaveBalance.remaining} gün, ` +
          `talep edilen: ${requestedDays} gün.`
        );
        return;
      }
    }

    console.log('✅ Validasyonlar geçti, Firestore\'a kaydediliyor...');
    setLeaveLoading(true);
    try {
      let fileMeta = null;
      if (leaveReportFile) {
        const ext = String(leaveReportFile.name).split('.').pop();
        const path = `leave-requests/${auth.currentUser.uid}/${Date.now()}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, leaveReportFile);
        const url = await getDownloadURL(storageRef);
        fileMeta = { name: leaveReportFile.name, path, url, type: leaveReportFile.type || ext };
      }

      const leaveRequestData = {
        employeeId: employeeId || auth.currentUser.uid, // Önce employeeId, yoksa auth UID
        employeeName: `${userData.eName} ${userData.eSurname}`,
        employeeEmail: userData.eMail,
        leaveType: selectedLeaveType,
        startDate: leaveStartDate,
        endDate: leaveEndDate,
        leaveDays: Number(leaveDays),
        travelDays: Number(travelDays),
        reason: leaveReason,
        address: leaveAddress,
        reportFile: fileMeta,
        status: 'PENDING', // Beklemede
        createdAt: new Date(),
        requestType: 'LEAVE_REQUEST' // İzin talebi olduğunu belirtmek için
      };

      console.log('📝 Firestore\'a kaydediliyor:', leaveRequestData);
      const docRef = await addDoc(collection(db, 'LeaveRequests'), leaveRequestData);
      console.log('✅ Leave request saved with ID:', docRef.id);
      console.log('📊 Leave request data:', leaveRequestData);
      console.log('👤 Using employeeId:', employeeId || auth.currentUser.uid);
      
      // Admin'lere mail gönder (geçici olarak devre dışı)
      // TODO: Firebase Functions deploy edildikten sonra aktif et
      /*
      try {
        const response = await fetch(
          'https://us-central1-habcare-8ecad.cloudfunctions.net/sendLeaveRequestNotification',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              employeeName: `${userData.eName} ${userData.eSurname}`,
              employeeEmail: userData.eMail,
              leaveType: selectedLeaveType,
              startDate: leaveStartDate,
              endDate: leaveEndDate,
              leaveDays: Number(leaveDays),
              reason: leaveReason,
              address: leaveAddress
            }),
          }
        );

        if (response.ok) {
          console.log('Leave request notification sent successfully');
        } else {
          console.error('Failed to send leave request notification');
        }
      } catch (error) {
        console.error('Error sending leave request notification:', error);
        // Mail gönderilemese bile form kaydedildi, kullanıcıya bildir
      }
      */
      
      toast.success('🎉 İzin talebiniz başarıyla gönderildi! Admin tarafından değerlendirilecektir.');
      
      // İzin talepleri listesini yenile
      fetchMyLeaveRequests();
      
      // Formu temizle
      setSelectedLeaveType('yillik');
      setLeaveStartDate('');
      setLeaveEndDate('');
      setLeaveDays('0');
      setTravelDays('0');
      setLeaveReason('');
      setLeaveAddress('');
      setLeaveReportFile(null);
      
    } catch (error) {
      console.error('❌ İzin talebi gönderme hatası:', error);
      console.error('❌ Hata detayları:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      toast.error('❌ İzin talebi gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    }
    console.log('🏁 İzin talebi gönderme işlemi tamamlandı');
    setLeaveLoading(false);
  };

  const closeSOPModal = () => {
    setShowSOPModal(false);
    setSelectedSOP(null);
    setHasScrolledToBottom(false); // Reset scroll state when modal closes
  };

  // Load all document data (SOPs, Forms, Educations) in a single parallel batch
  useEffect(() => {
    if (!userData.role) return;

    // Fire off view-tracking fetches (fire-and-forget, no dependencies between them)
    fetchViewedSOPs();
    fetchReadSOPs();
    fetchViewedForms();
    fetchViewedIndicationEducations();

    // Load all content data in parallel
    const loadAllDocumentData = async () => {
      const sopsRef = collection(db, 'SOPs');
      const formsRef = collection(db, 'Forms');

      try {
        await Promise.all([
          // SOP data
          (async () => {
            const [roleSnapshot, hepsiSnapshot] = await Promise.all([
              getDocs(query(sopsRef, where('group', 'array-contains', userData.role))),
              getDocs(query(sopsRef, where('group', 'array-contains', 'Hepsi')))
            ]);
            const sopsMap = new Map();
            roleSnapshot.docs.forEach(doc => { sopsMap.set(doc.id, { id: doc.id, ...doc.data() }); });
            hepsiSnapshot.docs.forEach(doc => { sopsMap.set(doc.id, { id: doc.id, ...doc.data() }); });
            setMainSOPList(Array.from(sopsMap.values()));
          })(),
          // Forms data
          (async () => {
            const [roleSnapshot, hepsiSnapshot] = await Promise.all([
              getDocs(query(formsRef, where('group', 'array-contains', userData.role))),
              getDocs(query(formsRef, where('group', 'array-contains', 'Hepsi')))
            ]);
            const formsMap = new Map();
            roleSnapshot.docs.forEach(doc => { formsMap.set(doc.id, { id: doc.id, ...doc.data() }); });
            hepsiSnapshot.docs.forEach(doc => { formsMap.set(doc.id, { id: doc.id, ...doc.data() }); });
            setFormsList(Array.from(formsMap.values()));
          })(),
          // Indication Educations data
          loadIndicationEducations()
        ]);
      } catch (error) {
        console.error("Error loading document data:", error);
      }
    };

    loadAllDocumentData();
  }, [userData.role]);

  // Auto-open SOP section if there are new unviewed SOPs (uses already-loaded state)
  useEffect(() => {
    if (mainSOPList.length > 0 && viewedSOPs.size > 0) {
      const hasNewSOPs = mainSOPList.some(sop => sop.newlyAdded && !viewedSOPs.has(sop.id));
      if (hasNewSOPs) {
        setShowMainSOPs(true);
      }
    }
  }, [mainSOPList, viewedSOPs]);

  // Auto-open Forms section if there are new unviewed forms (uses already-loaded state)
  useEffect(() => {
    if (formsList.length > 0 && viewedForms.size > 0) {
      const hasNewForms = formsList.some(form => form.newlyAdded && !viewedForms.has(form.id));
      if (hasNewForms) {
        setShowForms(true);
      }
    }
  }, [formsList, viewedForms]);

  const handleChangePassword = async () => {
    // Reset error
    setPasswordError('');
    
    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Lütfen tüm alanları doldurunuz.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Yeni şifreler eşleşmiyor.');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    
    try {
      const user = auth.currentUser;
      
      // Firebase requires recent authentication before changing password
      const credential = EmailAuthProvider.credential(
        user.email, 
        currentPassword
      );
      
      // Re-authenticate
      await reauthenticateWithCredential(user, credential);
      
      // Change password
      await updatePassword(user, newPassword);
      
      // Success
      toast.success('Şifreniz başarıyla değiştirildi.');
      setShowPasswordModal(false);
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Password change error:', error);
      
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Mevcut şifreniz yanlış.');
      } else {
        setPasswordError('Şifre değiştirme işlemi başarısız oldu. Lütfen tekrar deneyin.');
      }
    }
  };

  // Tab groups configuration
  const tabGroups = {
    dosyalar: {
      label: 'Dosyalar',
      icon: <FaFileAlt />,
      tabs: null // Tek tab, alt grup yok
    },
    'takvim-ajanda': {
      label: 'Takvim & Ajanda',
      icon: <FaCalendarAlt />,
      tabs: {
        takvim: { label: 'Takvim', icon: <FaCalendarAlt /> },
        ajandam: { label: 'Ajandam', icon: <FaListAlt /> }
      }
    },
    'iletisim-bildirim': {
      label: 'İletişim & Bildirim',
      icon: <FaBell />,
      tabs: {
        bildirimler: { label: 'Bildirimler', icon: <FaBell /> },
        'sistem-hata-bildirimleri': { label: 'Sistem Hata Bildirimleri', icon: <FaBug /> },
        mesajlar: { label: 'Mesajlar', icon: <FaComments /> }
      }
    },
    'izin-yonetimi': {
      label: 'İzin Yönetimi',
      icon: <FaCalendarAlt />,
      tabs: {
        'izin-talep': { label: 'İzin Talep Et', icon: <FaCalendarAlt /> },
        'izin-taleplerim': { label: 'İzin Taleplerim', icon: <FaListAlt /> }
      }
    },
    'dilekce-yonetimi': {
      label: 'Dilekçe Yönetimi',
      icon: <FaFileAlt />,
      tabs: {
        'sikayetlerim': { label: 'Dilekçelerim', icon: <FaFileAlt /> },
        'gelen-sikayetler': { label: 'Gelen Dilekçeler', icon: <FaFileAlt /> }
      }
    },
    'masraf-yonetimi': {
      label: 'Masraf Yönetimi',
      icon: <FaRegMoneyBillAlt />,
      tabs: {
        'masraf-talep': { label: 'Masraf Talep Et', icon: <FaRegMoneyBillAlt /> },
        'masraf-takip': { label: 'Masraf Takip', icon: <FaListAlt /> },
        'masraf-talepleri': { label: 'Masraf Talepleri', icon: <FaRegMoneyBillAlt /> }
      }
    },
    'cv-yonetimi': {
      label: 'CV/GCP/IATA',
      icon: <FaUser />,
      tabs: {
        'cv-tr': { label: 'Türkçe CV', icon: <FaUser /> },
        'cv-en': { label: 'İngilizce CV', icon: <FaUser /> },
        'gcp': { label: 'GCP', icon: <FaFileAlt /> },
        'iata': { label: 'IATA', icon: <FaFileAlt /> }
      }
    },
    oyun: {
      label: 'Oyun',
      icon: <FaFutbol />,
      tabs: null // Tek tab, alt grup yok
    }
  };

  // Tab grup yönetimi fonksiyonları
  const handleTabGroupClick = (groupKey) => {
    const group = tabGroups[groupKey];

    // Gizli oyun kilidi: Dosyalar'a arka arkaya 3 tıklama (sadece izinli kullanıcılar)
    if (groupKey === 'dosyalar' && isGamePlayer && !gameUnlocked) {
      gameClickCountRef.current += 1;
      if (gameClickCountRef.current >= 3) {
        setGameUnlocked(true);
        toast.success('⚽ Habcare Gol kilidi açıldı!');
      }
    } else if (groupKey !== 'dosyalar') {
      gameClickCountRef.current = 0;
    }

    if (group.tabs) {
      // Alt tabları olan grup
      setActiveTabGroup(groupKey);
      
      // İlk alt tabı seç (eğer henüz seçili değilse)
      if (!activeSubTab || !Object.keys(group.tabs).includes(activeSubTab)) {
        const firstSubTab = Object.keys(group.tabs)[0];
        setActiveSubTab(firstSubTab);
        setActiveTab(firstSubTab);
      }
    } else {
      // Tek tab (dosyalar gibi)
      setActiveTabGroup(groupKey);
      setActiveSubTab('');
      setActiveTab(groupKey);
    }
  };

  const handleSubTabClick = (subTabKey) => {
    setActiveSubTab(subTabKey);
    setActiveTab(subTabKey);
  };

  // Mevcut activeTab'a göre grup ve alt tab'ı belirle
  const getCurrentTabGroup = () => {
    for (const [groupKey, group] of Object.entries(tabGroups)) {
      if (groupKey === activeTab) {
        return { groupKey, subTabKey: null };
      }
      if (group.tabs && Object.keys(group.tabs).includes(activeTab)) {
        return { groupKey, subTabKey: activeTab };
      }
    }
    return { groupKey: 'dosyalar', subTabKey: null };
  };

  // Component mount olduğunda mevcut tab'a göre grup ayarla
  useEffect(() => {
    const { groupKey, subTabKey } = getCurrentTabGroup();
    setActiveTabGroup(groupKey);
    setActiveSubTab(subTabKey || '');
  }, []);

  // Tab icons mapping
  const tabIcons = {
    dosyalar: <FaFileAlt />,
    bildirimler: <FaBell />,
    takvim: <FaCalendarAlt />,
    ajandam: <FaListAlt />,
    'sistem-hata-bildirimleri': <FaBug />,
    mesajlar: <FaComments />,
    'izin-talep': <FaCalendarAlt />,
    'izin-taleplerim': <FaListAlt />,
    'sikayetlerim': <FaFileAlt />,
    'gelen-sikayetler': <FaFileAlt />,
    'masraf-talep': <FaRegMoneyBillAlt />,
    'masraf-takip': <FaListAlt />,
    'masraf-talepleri': <FaRegMoneyBillAlt />,
    'cv': <FaUser />,
    'gcp': <FaFileAlt />,
    'iata': <FaFileAlt />,
    oyun: <FaFutbol />
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className={styles.pageWrapper}>
        <Container fluid className={styles.profileContainer}>
          <div className={styles.dashboardHeader}>
            <div className={styles.welcomeSection}>
              <h2 className={styles.welcomeText}>
                Hoş Geldiniz, <span className={styles.userName}>{`${userData.eName} ${userData.eSurname}`}</span>
              </h2>
              <p className={styles.dateTime}>
                {new Date().toLocaleDateString('tr-TR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
        </div>

        <Row className={styles.mainContentRow}>
          <Col lg={3}>
            <div className={styles.sidebarContainer}>
              <div className={styles.profileSection}>
                <div className={styles.profileImageWrapper}>
                  <div className={styles.profileImageContainer}>
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className={styles.profileImage}
                      />
                    ) : (
                      <div className={styles.profileImagePlaceholder}>
                        <FaUser size={40} />
                      </div>
                    )}
                  </div>
                  <button 
                    className={styles.editIconButton} 
                    onClick={() => document.getElementById('fileInput').click()}
                    aria-label="Change profile picture"
                  >
                    <FaEdit size={16} />
                  </button>
                  <input
                    type="file"
                    id="fileInput"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                </div>
                
                <div className={styles.profileInfo}>
                  <h3 className={styles.profileName}>{`${userData.eName} ${userData.eSurname}`}</h3>
                  <span className={styles.profileBadge}>{userData.role}</span>
                </div>
              </div>

              <div className={styles.contactSection}>
                <h4 className={styles.sectionTitle}>İletişim Bilgileri</h4>
                <ul className={styles.contactList}>
                  <li className={styles.contactItem}>
                    <div className={styles.contactIconWrapper}>
                      <FaPhone className={styles.contactIcon} />
                    </div>
                    <div className={styles.contactTextContainer}>
                      <span className={styles.contactText}>{userData.phone || 'Telefon numarası yok'}</span>
                      {userData.emergencyPhone ? (
                        <div className={styles.emergencyPhoneRow}>
                          <span className={styles.contactTextSecondary}>
                            <strong>Acil Durum:</strong> {userData.emergencyPhone}
                          </span>
                          <button 
                            className={styles.editEmergencyPhoneButton}
                            onClick={handleOpenPhoneModal}
                            title="Acil Durum Telefonunu Düzenle"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      ) : (
                        <div className={styles.emergencyPhoneRow}>
                          <span className={styles.contactTextSecondary}>
                            <strong>Acil Durum:</strong> Eklenmemiş
                          </span>
                          <button 
                            className={styles.addEmergencyPhoneButton}
                            onClick={handleOpenPhoneModal}
                            title="Acil Durum Telefonu Ekle"
                          >
                            + Ekle
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                  <li className={styles.contactItem}>
                    <div className={styles.contactIconWrapper}>
                      <FaEnvelope className={styles.contactIcon} />
                    </div>
                    <span className={styles.contactText}>{userData.eMail || 'E-posta yok'}</span>
                  </li>
                  <li className={styles.contactItem}>
                    <div className={styles.contactIconWrapper}>
                      <FaHome className={styles.contactIcon} />
                    </div>
                    <span className={styles.contactText} title="Ev Adresi">
                      {userData.address || 'Ev adresi bilgisi yok'}
                    </span>
                    <button 
                      className={styles.editAddressButton}
                      onClick={handleOpenAddressModal}
                      title="Adres Bilgilerini Düzenle"
                    >
                      <FaEdit />
                    </button>
                  </li>
                  <li className={styles.contactItem}>
                    <div className={styles.contactIconWrapper}>
                      <FaBuilding className={styles.contactIcon} />
                    </div>
                    <span className={styles.contactText} title="İş Adresi">
                      {userData.workAddress || 'İş adresi bilgisi yok'}
                    </span>
                    <button 
                      className={styles.editAddressButton}
                      onClick={handleOpenAddressModal}
                      title="Adres Bilgilerini Düzenle"
                    >
                      <FaEdit />
                    </button>
                  </li>
                  <li className={styles.contactItem}>
                    <div className={styles.contactIconWrapper}>
                      <FaUserTie className={styles.contactIcon} />
                    </div>
                    <span className={styles.contactText}>{userData.role || 'Rol yok'}</span>
                  </li>
                </ul>
                
                <button 
                  className={styles.passwordChangeButton}
                  onClick={() => setShowPasswordModal(true)}
                >
                  <FaLock className={styles.buttonIcon} />
                  Şifremi Değiştir
                </button>
              </div>

              <div className={styles.projectSection}>
                <div className={styles.projectSectionHeader}>
                  <h4 className={styles.sectionTitle}>Projelerim</h4>
                  <button 
                    className={styles.togglePassiveButton}
                    onClick={() => setShowPassiveProjects(!showPassiveProjects)}
                  >
                    <FaEyeSlash />
                    {showPassiveProjects ? 'Pasif Projeleri Gizle' : 'Pasif Projeleri Göster'}
                  </button>
                </div>
                <div className={styles.projectList}>
                  {userData.projects && userData.projects.length > 0 ? (
                    userData.projects
                      .filter(project => showPassiveProjects || !isPassiveProject(project))
                      .map((project, index) => (
                      <motion.div 
                        key={index}
                        className={`${styles.projectCard} ${project.projectStatus === 'Pasif' ? styles.passiveProject : ''}`}
                        whileHover={{ y: -5 }}
                        transition={{ type: 'spring', stiffness: 300 }}
                      >
                        <div className={styles.projectIcon}>
                          <FaProjectDiagram />
                        </div>
                        <div className={styles.projectInfo} onClick={() => handleProjectClick(project)}>
                          <h5 className={styles.projectTitle}>{project.titleShort}</h5>
                          <p className={styles.projectDate}>{project.projectDate}</p>
                          
                          {/* Worker Type ve Blind Status Bilgileri */}
                          <div className={styles.projectBadges}>
                            {project.workerType && (
                              <span className={`${styles.projectBadge} ${styles.workerTypeBadge} ${styles[project.workerType]}`}>
                                {project.workerType === 'primer' ? 'Primer' : 'Backup'}
                              </span>
                            )}
                            {project.blindStatus && (
                              <span className={`${styles.projectBadge} ${styles.blindStatusBadge} ${styles[project.blindStatus]}`}>
                                {project.blindStatus === 'blinded' ? 'Blinded' : 'Unblinded'}
                              </span>
                            )}
                            {(!project.workerType || !project.blindStatus) && (
                              <span className={`${styles.projectBadge} ${styles.missingInfoBadge}`}>
                                Bilgi Eksik
                              </span>
                            )}
                          </div>
                          
                          {isPassiveProject(project) && (
                            <span
                              className={styles.passiveBadge}
                              title={
                                project.status === 'passive'
                                  ? 'Bu projedeki göreviniz pasif'
                                  : 'Proje pasif durumda'
                              }
                            >
                              Pasif
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <p className={styles.emptyMessage}>Atanmış projeniz bulunmamaktadır.</p>
                  )}
                </div>
              </div>
            </div>
          </Col>
          
          <Col lg={9}>
            <div className={styles.mainContent}>
              <div className={styles.tabContainer}>
                {/* Ana Tab Grupları */}
                <nav className={styles.tabGroupNavigation}>
                  {Object.entries(tabGroups).map(([groupKey, group]) => {
                    // Yetki kontrolü
                    if (groupKey === 'dilekce-yonetimi' && !isUserAdmin && !isComplaintTarget) return null;
                    if (groupKey === 'oyun' && (!isGamePlayer || !gameUnlocked)) return null;

                    return (
                      <button
                        key={groupKey}
                        className={`${styles.tabGroupButton} ${activeTabGroup === groupKey ? styles.activeTabGroup : ''}`}
                        onClick={() => handleTabGroupClick(groupKey)}
                      >
                        <span className={styles.tabIcon}>{group.icon}</span>
                        <span className={styles.tabLabel}>{group.label}</span>
                        {groupKey === 'iletisim-bildirim' && totalUnreadMessages > 0 && (
                          <span className={styles.notificationBadge}>{totalUnreadMessages}</span>
                        )}
                      </button>
                    );
                  })}
                </nav>

                {/* Alt Tab Navigasyonu */}
                {tabGroups[activeTabGroup]?.tabs && (
                  <nav className={styles.subTabNavigation}>
                    {Object.entries(tabGroups[activeTabGroup].tabs).map(([subTabKey, subTab]) => {
                      // Yetki kontrolü
                      if (subTabKey === 'gelen-sikayetler' && !isUserAdmin && !isComplaintTarget) return null;
                      if (subTabKey === 'masraf-talepleri' && !isUserAdmin) return null;
                      
                      return (
                        <button
                          key={subTabKey}
                          className={`${styles.subTabButton} ${activeSubTab === subTabKey ? styles.activeSubTab : ''}`}
                          onClick={() => handleSubTabClick(subTabKey)}
                        >
                          <span className={styles.tabIcon}>{subTab.icon}</span>
                          <span className={styles.tabLabel}>{subTab.label}</span>
                          {subTabKey === 'mesajlar' && totalUnreadMessages > 0 && (
                            <span className={styles.notificationBadge}>{totalUnreadMessages}</span>
                          )}
                        </button>
                      );
                    })}
                  </nav>
                )}
                
                <div className={styles.tabContent}>
                  {/* Turkish CV Tab */}
                  {activeTab === 'cv-tr' && (
                    <CVManagementModal 
                      show={true}
                      onHide={() => {}}
                      employeeId={employeeId}
                      userData={userData}
                      isInline={true}
                      documentType="CV-TR"
                    />
                  )}

                  {/* English CV Tab */}
                  {activeTab === 'cv-en' && (
                    <CVManagementModal 
                      show={true}
                      onHide={() => {}}
                      employeeId={employeeId}
                      userData={userData}
                      isInline={true}
                      documentType="CV-EN"
                    />
                  )}

                  {/* GCP Tab */}
                  {activeTab === 'gcp' && (
                    <CVManagementModal 
                      show={true}
                      onHide={() => {}}
                      employeeId={employeeId}
                      userData={userData}
                      isInline={true}
                      documentType="GCP"
                    />
                  )}

                  {/* IATA Tab */}
                  {activeTab === 'iata' && (
                    <CVManagementModal 
                      show={true}
                      onHide={() => {}}
                      employeeId={employeeId}
                      userData={userData}
                      isInline={true}
                      documentType="IATA"
                      allowMultiple={true}
                    />
                  )}

                  {activeTab === 'oyun' && isGamePlayer && gameUnlocked && (
                    <GridStriker
                      onClose={() => {
                        setActiveTabGroup('dosyalar');
                        setActiveSubTab('');
                        setActiveTab('dosyalar');
                      }}
                    />
                  )}
                  {activeTab === 'dosyalar' && (
                    <div className={styles.filesTabContent}>
                      <div className={styles.fileCardsRow}>
                        <motion.div 
                          className={`${styles.fileCard} ${styles.sopCard} ${showMainSOPs ? styles.activeFileCard : ''}`}
                          onClick={handleMainSOPCardClick}
                          onKeyDown={(event) => handleFileSectionKeyDown(event, 'sop')}
                          role="button"
                          tabIndex={0}
                          aria-expanded={showMainSOPs}
                          aria-controls="sop-file-section"
                          whileHover={{ scale: 1.03 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                        >
                          <div className={styles.fileCardIcon}>
                            <FaFileAlt size={24} />
                          </div>
                          <div className={styles.fileCardContent}>
                            <h3 className={styles.fileCardTitle}>SOP Dosyaları</h3>
                            <p className={styles.fileCardDesc}>Standart Operasyon Prosedürleri</p>
                          </div>
                        </motion.div>

                        <motion.div
                          className={`${styles.fileCard} ${styles.educationCard} ${showEducationTitles ? styles.activeFileCard : ''}`}
                          onClick={() => handleFileSectionToggle('education')}
                          onKeyDown={(event) => handleFileSectionKeyDown(event, 'education')}
                          role="button"
                          tabIndex={0}
                          aria-expanded={showEducationTitles}
                          aria-controls="education-file-section"
                          whileHover={{ scale: 1.03 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                        >
                          <div className={styles.fileCardIcon}>
                            <FaBook size={24} />
                          </div>
                          <div className={styles.fileCardContent}>
                            <h3 className={styles.fileCardTitle}>Eğitim Başlıkları</h3>
                            <p className={styles.fileCardDesc}>Eğitim ve gelişim materyalleri</p>
                          </div>
                        </motion.div>

                        <motion.div
                          className={`${styles.fileCard} ${styles.formsCard} ${showForms ? styles.activeFileCard : ''}`}
                          onClick={handleFormsCardClick}
                          onKeyDown={(event) => handleFileSectionKeyDown(event, 'forms')}
                          role="button"
                          tabIndex={0}
                          aria-expanded={showForms}
                          aria-controls="forms-file-section"
                          whileHover={{ scale: 1.03 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                        >
                          <div className={styles.fileCardIcon}>
                            <FaFileAlt size={24} />
                          </div>
                          <div className={styles.fileCardContent}>
                            <h3 className={styles.fileCardTitle}>Formlar</h3>
                            <p className={styles.fileCardDesc}>Standart form ve belgeler</p>
                          </div>
                        </motion.div>
                      </div>

                      {/* Display expanded SOP files if active */}
                      {showMainSOPs && (
                        <motion.div 
                          className={styles.fileListContainer}
                          id="sop-file-section"
                          ref={(node) => { fileSectionRefs.current.sop = node; }}
                          tabIndex={-1}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.18 }}
                        >
                          <h3 className={styles.listTitle}>SOP Dosyaları</h3>
                          
                          {/* SOP Search Input */}
                          <div className={styles.searchContainer}>
                            <input
                              type="text"
                              placeholder="SOP ara..."
                              value={sopSearchTerm}
                              onChange={(e) => setSOPSearchTerm(e.target.value)}
                              className={styles.searchInput}
                            />
                            {sopSearchTerm && (
                              <button
                                className={styles.clearSearchBtn}
                                onClick={() => setSOPSearchTerm('')}
                                title="Aramayı temizle"
                              >
                                <FaTimes />
                              </button>
                            )}
                          </div>
                          
                          <div className={styles.fileListWrapper}>
                            <AnimatePresence>
                              {mainSOPList.filter(sop => 
                                sop.name.toLowerCase().includes(sopSearchTerm.toLowerCase())
                              ).length > 0 ? (
                                <ul className={styles.documentList}>
                                  {mainSOPList
                                    .filter(sop => 
                                      sop.name.toLowerCase().includes(sopSearchTerm.toLowerCase())
                                    )
                                    .map(sop => (
                                    <motion.li
                                      key={sop.id}
                                      className={`${styles.documentItem} ${sop.newlyAdded && !viewedSOPs.has(sop.id) ? styles.newDocument : ''} ${readSOPs.has(sop.id) ? styles.readDocument : ''}`}
                                      onClick={() => handleSOPItemClick(sop)}
                                      whileHover={{ x: 5 }}
                                      initial={sop.newlyAdded && !viewedSOPs.has(sop.id) ? { scale: 0.95, opacity: 0 } : {}}
                                      animate={{ scale: 1, opacity: 1 }}
                                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                    >
                                      <div className={styles.documentTypeIcon}>
                                        {sop.fileType === 'application/pdf' ? <FaFilePdf /> :
                                         sop.fileType === 'video/mp4' ? <FaVideo /> :
                                         sop.fileType === 'application/vnd.ms-powerpoint' || sop.fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ? <FaFilePowerpoint /> :
                                         <FaFileAlt />}
                                      </div>
                                      <div className={styles.documentInfo}>
                                        <span className={styles.documentName}>{sop.name}</span>
                                        <span className={styles.documentVersion}>v{sop.versionNumber}</span>
                                      </div>
                                      {readSOPs.has(sop.id) && (
                                        <span className={styles.readBadge}>
                                          <FaCheckCircle /> Okundu
                                        </span>
                                      )}
                                      {sop.newlyAdded && !viewedSOPs.has(sop.id) && (
                                        <motion.span
                                          className={styles.newBadge}
                                          initial={{ scale: 0 }}
                                          animate={{ scale: 1 }}
                                          transition={{ delay: 0.2 }}
                                        >
                                          Yeni
                                        </motion.span>
                                      )}
                                    </motion.li>
                                  ))}
                                </ul>
                              ) : (
                                <p className={styles.emptyMessage}>
                                  {sopSearchTerm ? 'Arama kriterlerine uygun SOP bulunamadı.' : 'Erişilebilir SOP dosyası bulunmamaktadır.'}
                                </p>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      )}

                      {/* Display education titles if active */}
                      {showEducationTitles && (
                        <motion.div 
                          className={styles.fileListContainer}
                          id="education-file-section"
                          ref={(node) => { fileSectionRefs.current.education = node; }}
                          tabIndex={-1}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.18 }}
                        >
                          <h3 className={styles.listTitle}>Eğitim Başlıkları</h3>
                          
                          {/* Education Search Input */}
                          <div className={styles.searchContainer}>
                            <input
                              type="text"
                              placeholder="Eğitim ara..."
                              value={educationSearchTerm}
                              onChange={(e) => setEducationSearchTerm(e.target.value)}
                              className={styles.searchInput}
                            />
                            {educationSearchTerm && (
                              <button
                                className={styles.clearSearchBtn}
                                onClick={() => setEducationSearchTerm('')}
                                title="Aramayı temizle"
                              >
                                <FaTimes />
                              </button>
                            )}
                          </div>
                          
                          <div className={styles.fileListWrapper}>
                            <AnimatePresence>
                              {indicationEducations.filter(education =>
                                education.title.toLowerCase().includes(educationSearchTerm.toLowerCase())
                              ).length > 0 ? (
                                <ul className={styles.documentList}>
                                  {indicationEducations
                                    .filter(education =>
                                      education.title.toLowerCase().includes(educationSearchTerm.toLowerCase())
                                    )
                                    .map(education => {
                                    const isExpired = education.hasDeadline && education.deadline && new Date(education.deadline) < new Date();
                                    const deadlineDate = education.hasDeadline && education.deadline ? new Date(education.deadline) : null;
                                    const daysLeft = deadlineDate ? Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

                                    return (
                                    <motion.li
                                      key={education.id}
                                      className={`${styles.documentItem} ${styles.educationItem} ${education.newlyAdded && !viewedIndicationEducations.has(education.id) ? styles.newDocument : ''}`}
                                      onClick={() => !isExpired && handleIndicationEducationClick(education)}
                                      whileHover={isExpired ? {} : { x: 5 }}
                                      initial={education.newlyAdded && !viewedIndicationEducations.has(education.id) ? { scale: 0.95, opacity: 0 } : {}}
                                      animate={{ scale: 1, opacity: 1 }}
                                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                      style={isExpired ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                    >
                                      <div className={styles.documentTypeIcon}>
                                        <FaVideo />
                                      </div>
                                      <div className={styles.documentInfo}>
                                        <span className={styles.documentName}>{education.title}</span>
                                        {education.estimatedDuration && (
                                          <span className={styles.documentVersion}>{education.estimatedDuration}</span>
                                        )}
                                        {deadlineDate && !isExpired && (
                                          <span className={styles.documentVersion} style={{
                                            color: daysLeft <= 3 ? '#dc2626' : daysLeft <= 7 ? '#d97706' : '#0284c7',
                                            fontWeight: 500
                                          }}>
                                            <FaCalendarAlt style={{ marginRight: '4px', fontSize: '0.7rem' }} />
                                            Son Katılma: {deadlineDate.toLocaleDateString('tr-TR')}
                                          </span>
                                        )}
                                      </div>
                                      {isExpired && (
                                        <span className={styles.completedBadge} style={{ background: '#fee2e2', color: '#dc2626' }}>
                                          <FaTimesCircle /> Süre Doldu
                                        </span>
                                      )}
                                      {!isExpired && viewedIndicationEducations.has(education.id) && (
                                        <span className={styles.completedBadge}>
                                          <FaCheckCircle /> Tamamlandı
                                        </span>
                                      )}
                                      {!isExpired && education.newlyAdded && !viewedIndicationEducations.has(education.id) && (
                                        <motion.span
                                          className={styles.newBadge}
                                          initial={{ scale: 0 }}
                                          animate={{ scale: 1 }}
                                          transition={{ delay: 0.2 }}
                                        >
                                          Yeni
                                        </motion.span>
                                      )}
                                    </motion.li>
                                    );
                                  })}
                                </ul>
                              ) : (
                                <p className={styles.emptyMessage}>
                                  {educationSearchTerm ? 'Arama kriterlerine uygun eğitim bulunamadı.' : 'Henüz atanmış eğitim bulunmamaktadır.'}
                                </p>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      )}

                      {/* Display forms if active */}
                      {showForms && (
                        <motion.div
                          className={styles.fileListContainer}
                          id="forms-file-section"
                          ref={(node) => { fileSectionRefs.current.forms = node; }}
                          tabIndex={-1}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.18 }}
                        >
                          <h3 className={styles.listTitle}>Formlar</h3>

                          {/* Forms Search Input */}
                          <div className={styles.searchContainer}>
                            <input
                              type="text"
                              placeholder="Form ara..."
                              value={formsSearchTerm}
                              onChange={(e) => setFormsSearchTerm(e.target.value)}
                              className={styles.searchInput}
                            />
                            {formsSearchTerm && (
                              <button
                                className={styles.clearSearchBtn}
                                onClick={() => setFormsSearchTerm('')}
                                title="Aramayı temizle"
                              >
                                <FaTimes />
                              </button>
                            )}
                          </div>

                          <div className={styles.fileListWrapper}>
                            <AnimatePresence>
                              {filteredFormsList.length > 0 ? (
                                <ul className={styles.documentList}>
                                  {filteredFormsList.map(form => (
                                    <motion.li
                                      key={form.id}
                                      className={`${styles.documentItem} ${form.newlyAdded && !viewedForms.has(form.id) ? styles.newDocument : ''}`}
                                      onClick={() => handleFormItemClick(form)}
                                      whileHover={{ x: 5 }}
                                      initial={form.newlyAdded && !viewedForms.has(form.id) ? { scale: 0.95, opacity: 0 } : {}}
                                      animate={{ scale: 1, opacity: 1 }}
                                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                    >
                                      <div className={styles.documentTypeIcon}>
                                        <FaFileAlt />
                                      </div>
                                      <div className={styles.documentInfo}>
                                        <span className={styles.documentName}>{form.name}</span>
                                        {form.versionNumber && (
                                          <span className={styles.documentVersion}>v{form.versionNumber}</span>
                                        )}
                                      </div>
                                      {form.newlyAdded && !viewedForms.has(form.id) && (
                                        <motion.span
                                          className={styles.newBadge}
                                          initial={{ scale: 0 }}
                                          animate={{ scale: 1 }}
                                          transition={{ delay: 0.2 }}
                                        >
                                          Yeni
                                        </motion.span>
                                      )}
                                    </motion.li>
                                  ))}
                                </ul>
                              ) : (
                                <p className={styles.emptyMessage}>
                                  {formsSearchTerm
                                    ? `"${formsSearchTerm}" ile eşleşen form bulunamadı.`
                                    : 'Erişilebilir form bulunmamaktadır.'}
                                </p>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      )}


                    </div>
                  )}

                  {activeTab === 'bildirimler' && (
                    <div className={styles.notificationsTabContent}>
                      <Notifications isAdmin={userData.role === 'admin'} />
                    </div>
                  )}

                  {activeTab === 'takvim' && (
                    <div className={styles.calendarTabContent}>
                      <PersonalCalendar />
                    </div>
                  )}

                  {activeTab === 'ajandam' && (
                    <div className={styles.agendaTabContent}>
                      <Agenda />
                    </div>
                  )}

                  {activeTab === 'sistem-hata-bildirimleri' && (
                    <div className={styles.errorReportTabContent}>
                      <div className={styles.errorReportCard}>
                        <h3 className={styles.cardTitle}>Sistem Hata Bildirimi</h3>
                        <p className={styles.cardDescription}>
                          Sistemde karşılaştığınız hataları aşağıdaki form ile bildirebilirsiniz.
                        </p>
                        
                        <Form className={styles.errorForm}>
                          <Form.Group controlId="errorSummary">
                            <Form.Label className={styles.formLabel}>Hata Açıklaması</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              value={errorSummary}
                              onChange={(e) => setErrorSummary(e.target.value)}
                              placeholder="Karşılaştığınız hatayı detaylı bir şekilde açıklayınız..."
                              className={styles.formControl}
                            />
                          </Form.Group>

                          <Form.Group controlId="screenshotFile" className="mt-4">
                            <Form.Label className={styles.formLabel}>Ekran Görüntüsü (İsteğe Bağlı)</Form.Label>
                            <div className={styles.fileUploadContainer}>
                              <label htmlFor="screenshotUpload" className={styles.fileUploadLabel}>
                                <FaEdit className={styles.uploadIcon} />
                                <span>Dosya Seç</span>
                              </label>
                              <input
                                id="screenshotUpload"
                                type="file"
                                accept="image/*"
                                onChange={(e) => setScreenshotFile(e.target.files[0])}
                                className={styles.fileUploadInput}
                              />
                              <span className={styles.fileSelectedName}>
                                {screenshotFile ? screenshotFile.name : 'Dosya seçilmedi'}
                              </span>
                            </div>
                          </Form.Group>

                          <Button 
                            className={styles.submitButton} 
                            onClick={handleErrorFeedbackSubmit}
                          >
                            <FaCheckCircle className={styles.buttonIcon} />
                            Bildirimi Gönder
                          </Button>
                        </Form>
                      </div>
                    </div>
                  )}

                  {activeTab === 'mesajlar' && (
                    <div className={styles.messagesTabContent}>
                      {!messengerLoaded ? (
                        <div className={styles.messengerGateway}>
                          <div className={styles.gatewayIcon}>
                            <FaComments size={48} />
                          </div>
                          <h3 className={styles.gatewayTitle}>Mesajlar</h3>
                          <p className={styles.gatewayDescription}>
                            Çalışanlarla iletişim kurun, dosya paylaşın ve toplantılar oluşturun.
                          </p>
                          {totalUnreadMessages > 0 && (
                            <div className={styles.gatewayUnreadBadge}>
                              <FaEnvelope size={16} />
                              <span>{totalUnreadMessages} okunmamış mesajınız var</span>
                            </div>
                          )}
                          <button
                            className={styles.gatewayLoadButton}
                            onClick={() => setMessengerLoaded(true)}
                          >
                            <FaComments size={16} />
                            <span>Mesajları Yükle</span>
                          </button>
                        </div>
                      ) : (
                        <Suspense fallback={
                          <div className={styles.messengerLoading}>
                            <FaSpinner className={styles.spinnerIcon} size={32} />
                            <p>Mesajlar yükleniyor...</p>
                          </div>
                        }>
                          <Messenger users={users} deferLoad />
                        </Suspense>
                      )}
                    </div>
                  )}

                  {activeTab === 'izin-taleplerim' && (
                    <div className={styles.myLeaveRequestsTabContent}>
                      <div className={styles.leaveRequestsCard}>
                        <h3 className={styles.cardTitle}>İzin Taleplerim</h3>
                        <p className={styles.cardDescription}>
                          Geçmiş izin taleplerinizi görüntüleyebilir ve durumlarını takip edebilirsiniz.
                        </p>
                        
                        {leaveRequestsLoading ? (
                          <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Yükleniyor...</span>
                            </div>
                          </div>
                        ) : myLeaveRequests.length === 0 ? (
                          <div className="text-center py-5">
                            <FaExclamationTriangle className="text-muted mb-3" size={48} />
                            <p className="text-muted">Henüz izin talebiniz bulunmamaktadır.</p>
                            <p className="text-muted">İzin talep etmek için "İzin Talep Et" sekmesini kullanabilirsiniz.</p>
                          </div>
                        ) : (
                          <>
                            <div className={styles.leaveRequestsList}>
                              {currentRequests.map((request) => (
                              <div key={request.id} className={styles.leaveRequestItem}>
                                <div className={styles.leaveRequestHeader}>
                                  <div className={styles.leaveRequestInfo}>
                                    <h5 className={styles.leaveRequestTitle}>
                                      {leaveTypeTranslations[request.leaveType] || request.leaveType}
                                    </h5>
                                    <p className={styles.leaveRequestDate}>
                                      {(() => {
                                        try {
                                          if (request.createdAt?.toDate) {
                                            return request.createdAt.toDate().toLocaleDateString('tr-TR');
                                          } else if (request.createdAt) {
                                            return new Date(request.createdAt).toLocaleDateString('tr-TR');
                                          } else {
                                            return 'Tarih bilinmiyor';
                                          }
                                        } catch (e) {
                                          return 'Tarih hatası';
                                        }
                                      })()} tarihinde gönderildi
                                    </p>
                                  </div>
                                  <div className={styles.leaveRequestStatus}>
                                    {request.status === 'PENDING' && (
                                      <span className={`${styles.statusBadge} ${styles.statusPending}`}>
                                        <FaClock className="me-1" />
                                        Beklemede
                                      </span>
                                    )}
                                    {request.status === 'APPROVED' && (
                                      <span className={`${styles.statusBadge} ${styles.statusApproved}`}>
                                        <FaCheckCircle className="me-1" />
                                        Onaylandı
                                      </span>
                                    )}
                                    {request.status === 'REJECTED' && (
                                      <span className={`${styles.statusBadge} ${styles.statusRejected}`}>
                                        <FaTimesCircle className="me-1" />
                                        Reddedildi
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                <div className={styles.leaveRequestDetails}>
                                  <div className="row">
                                    <div className="col-md-3">
                                      <strong>Başlangıç:</strong><br />
                                      {new Date(request.startDate).toLocaleDateString('tr-TR')}
                                    </div>
                                    <div className="col-md-3">
                                      <strong>Bitiş:</strong><br />
                                      {new Date(request.endDate).toLocaleDateString('tr-TR')}
                                    </div>
                                    <div className="col-md-3">
                                      <strong>Süre:</strong><br />
                                      {request.leaveDays} gün
                                    </div>
                                    <div className="col-md-3">
                                      <strong>Yol Süresi:</strong><br />
                                      {request.travelDays} gün
                                    </div>
                                  </div>
                                  
                                  {request.reason && (
                                    <div className="mt-3">
                                      <strong>İzin Nedeni:</strong><br />
                                      <p className="mb-0">{request.reason}</p>
                                    </div>
                                  )}
                                  
                                  {request.address && (
                                    <div className="mt-2">
                                      <strong>Adres:</strong><br />
                                      <p className="mb-0">{request.address}</p>
                                    </div>
                                  )}
                                  
                                  {request.adminNote && (
                                    <div className="mt-3">
                                      <strong>Yönetici Notu:</strong><br />
                                      <div className={`${styles.adminNote} ${
                                        request.status === 'APPROVED' ? styles.adminNoteApproved : 
                                        request.status === 'REJECTED' ? styles.adminNoteRejected : ''
                                      }`}>
                                        {request.adminNote}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {request.processedBy && (
                                    <div className="mt-2">
                                      <small className="text-muted">
                                        {request.status === 'PENDING' ? 'Henüz değerlendirilmedi' : 
                                         `Değerlendiren: ${request.processedBy}`}
                                      </small>
                                    </div>
                                  )}

                                  {request.source && (
                                    <div className="mt-2">
                                      <strong>Kaynak:</strong><br />
                                      <span className={`${styles.sourceBadge} ${
                                        request.source === 'LeaveRequests' ? styles.sourceRequest : styles.sourceAdmin
                                      }`}>
                                        {request.source === 'LeaveRequests' ? '📋 İzin Talebi' : '📄 Admin Oluşturdu'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            </div>

                            {/* Sayfalama Kontrolleri */}
                            {totalPages > 1 && (
                              <div className={styles.paginationContainer}>
                                <div className={styles.paginationInfo}>
                                  <span>
                                    {startIndex + 1}-{Math.min(endIndex, myLeaveRequests.length)} / {myLeaveRequests.length} izin talebi
                                  </span>
                                </div>

                                <div className={styles.paginationControls}>
                                  <button
                                    className={`${styles.paginationButton} ${styles.prevButton}`}
                                    onClick={goToPrevPage}
                                    disabled={currentPage === 1}
                                  >
                                    ← Önceki
                                  </button>

                                  <div className={styles.pageNumbers}>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                      <button
                                        key={page}
                                        className={`${styles.pageNumberButton} ${currentPage === page ? styles.active : ''}`}
                                        onClick={() => goToPage(page)}
                                      >
                                        {page}
                                      </button>
                                    ))}
                                  </div>

                                  <button
                                    className={`${styles.paginationButton} ${styles.nextButton}`}
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                  >
                                    Sonraki →
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'izin-talep' && (
                    <div className={styles.leaveRequestTabContent}>
                      <div className={styles.leaveRequestCard}>
                        <h3 className={styles.cardTitle}>İzin Talep Formu</h3>
                        <p className={styles.cardDescription}>
                          İzin talebinizi aşağıdaki form ile oluşturabilirsiniz. Talebiniz onay için ilgili kişilere iletilecektir.
                        </p>

                        {/* Yıllık izin bakiyesi — yalnızca yıllık izin seçiliyken gösterilir */}
                        {selectedLeaveType === 'yillik' && (
                          <div className={styles.leaveBalanceCard}>
                            <div className={styles.leaveBalanceItem}>
                              <span className={styles.leaveBalanceValue}>{annualLeaveBalance.entitlement}</span>
                              <span className={styles.leaveBalanceLabel}>Toplam Hak</span>
                            </div>
                            <div className={styles.leaveBalanceItem}>
                              <span className={styles.leaveBalanceValue}>{annualLeaveBalance.used}</span>
                              <span className={styles.leaveBalanceLabel}>Kullanılan</span>
                            </div>
                            <div className={styles.leaveBalanceItem}>
                              <span className={styles.leaveBalanceValue}>{annualLeaveBalance.reserved}</span>
                              <span className={styles.leaveBalanceLabel}>Bekleyen</span>
                            </div>
                            <div className={`${styles.leaveBalanceItem} ${annualLeaveBalance.remaining <= 0 ? styles.leaveBalanceDanger : styles.leaveBalanceRemaining}`}>
                              <span className={styles.leaveBalanceValue}>{annualLeaveBalance.remaining}</span>
                              <span className={styles.leaveBalanceLabel}>Kalan</span>
                            </div>
                          </div>
                        )}
                        {selectedLeaveType === 'yillik' && annualLeaveBalance.remaining <= 0 && (
                          <p className={styles.leaveBalanceWarning}>
                            <FaExclamationTriangle className="me-2" />
                            Kullanılabilir yıllık izin hakkınız bulunmuyor. Yeni talep gönderemezsiniz.
                          </p>
                        )}

                        <Form className={styles.leaveRequestForm}>
                          {/* İzin Türü */}
                          <Form.Group className="mb-4">
                            <Form.Label className={styles.formLabel}>İzin Türü *</Form.Label>
                            <div className={styles.leaveTypeOptions}>
                              <Form.Check
                                type="radio"
                                id="yillik"
                                name="leaveType"
                                label="Yıllık İzin"
                                checked={selectedLeaveType === 'yillik'}
                                onChange={() => setSelectedLeaveType('yillik')}
                                className={styles.radioOption}
                              />
                              <Form.Check
                                type="radio"
                                id="mazeret"
                                name="leaveType"
                                label="Mazeret İzni"
                                checked={selectedLeaveType === 'mazeret'}
                                onChange={() => setSelectedLeaveType('mazeret')}
                                className={styles.radioOption}
                              />
                              <Form.Check
                                type="radio"
                                id="raporlu"
                                name="leaveType"
                                label="Raporlu"
                                checked={selectedLeaveType === 'raporlu'}
                                onChange={() => setSelectedLeaveType('raporlu')}
                                className={styles.radioOption}
                              />
                            </div>
                          </Form.Group>

                          {/* Tarih Bilgileri */}
                          <Row className="mb-4">
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className={styles.formLabel}>Başlangıç Tarihi *</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={leaveStartDate}
                                  onChange={(e) => setLeaveStartDate(e.target.value)}
                                  required
                                  className={styles.formControl}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className={styles.formLabel}>Bitiş Tarihi *</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={leaveEndDate}
                                  onChange={(e) => setLeaveEndDate(e.target.value)}
                                  required
                                  className={styles.formControl}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* Süre Bilgileri */}
                          <Row className="mb-4">
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className={styles.formLabel}>İzin Süresi (gün)</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.5"
                                  min="0"
                                  value={leaveDays}
                                  readOnly
                                  title="Başlangıç ve bitiş tarihine göre otomatik hesaplanır (Cmt yarım, Paz hariç)"
                                  placeholder="0"
                                  className={styles.formControl}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className={styles.formLabel}>Yol Süresi (gün)</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.5"
                                  min="0"
                                  value={travelDays}
                                  onChange={(e) => setTravelDays(e.target.value)}
                                  placeholder="0"
                                  className={styles.formControl}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* İzin Nedeni */}
                          <Form.Group className="mb-4">
                            <Form.Label className={styles.formLabel}>İzin Nedeni</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={leaveReason}
                              onChange={(e) => setLeaveReason(e.target.value)}
                              placeholder="İzin nedeninizi açıklayınız..."
                              className={styles.formControl}
                            />
                          </Form.Group>

                          {/* Adres */}
                          <Form.Group className="mb-4">
                            <Form.Label className={styles.formLabel}>İzin Süresince Bulunacağınız Adres</Form.Label>
                            <Form.Control
                              type="text"
                              value={leaveAddress}
                              onChange={(e) => setLeaveAddress(e.target.value)}
                              placeholder="Adres bilginizi giriniz..."
                              className={styles.formControl}
                            />
                          </Form.Group>

                          {/* Rapor Dosyası */}
                          <Form.Group className="mb-4">
                            <Form.Label className={styles.formLabel}>Rapor Dosyası (İsteğe Bağlı)</Form.Label>
                            <div className={styles.fileUploadContainer}>
                              <label htmlFor="leaveReportUpload" className={styles.fileUploadLabel}>
                                <FaUpload className={styles.uploadIcon} />
                                <span>Dosya Seç</span>
                              </label>
                              <input
                                id="leaveReportUpload"
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={(e) => setLeaveReportFile(e.target.files[0])}
                                className={styles.fileUploadInput}
                              />
                              <span className={styles.fileSelectedName}>
                                {leaveReportFile ? leaveReportFile.name : 'Dosya seçilmedi'}
                              </span>
                            </div>
                          </Form.Group>

                          {/* Submit Button */}
                          <div className={styles.submitButtonContainer}>
                            <Button 
                              className={styles.submitButton} 
                              onClick={handleLeaveRequestSubmit}
                              disabled={leaveLoading}
                            >
                              {leaveLoading ? (
                                <>
                                  <FaSpinner className="spinner-border-sm me-2" />
                                  Gönderiliyor...
                                </>
                              ) : (
                                <>
                                  <FaSave className="me-2" />
                                  İzin Talebini Gönder
                                </>
                              )}
                            </Button>
                          </div>
                        </Form>
                      </div>
                    </div>
                  )}

                  {activeTab === 'sikayetlerim' && (
                    <div className={styles.errorReportTabContent}>
                      {/* Dilekçe Oluştur */}
                      <div className={styles.dilekceContainer}>
                        <div className={styles.dilekceHeader}>
                          <div className={styles.dilekceHeaderLeft}>
                            <img src="/habcare-logo.png" alt="HabcareCRO" className={styles.dilekceLogo} onError={(e) => e.target.style.display = 'none'} />
                            <span className={styles.dilekceCompany}>HabcareCRO</span>
                          </div>
                          <div className={styles.dilekceHeaderRight}>
                            <span className={styles.dilekceDate}>{formatDate(new Date())}</span>
                          </div>
                        </div>

                        <div className={styles.dilekceTitle}>
                          <h2>DİLEKÇE</h2>
                          <p className={styles.dilekceSubtitle}>Resmi Başvuru Formu</p>
                        </div>

                        <div className={styles.dilekceBody}>
                          {/* Muhatap Seçimi */}
                          <div className={styles.dilekceSection}>
                            <div className={styles.dilekceSectionTitle}>
                              <FaUserTie className={styles.dilekceSectionIcon} />
                              <span>MUHATAP (Kime Gönderilecek)</span>
                            </div>
                            <div className={styles.dilekceTargetGrid}>
                              {complaintTargets.map((target) => (
                                <div
                                  key={target.email}
                                  className={`${styles.dilekceTargetCard} ${selectedTargets.includes(target.email) ? styles.dilekceTargetSelected : ''} ${target.isTest ? styles.dilekceTargetTest : ''}`}
                                  onClick={() => toggleTarget(target.email)}
                                >
                                  <div className={styles.dilekceTargetCheck}>
                                    {selectedTargets.includes(target.email) ? <FaCheckCircle /> : <div className={styles.dilekceTargetCheckEmpty} />}
                                  </div>
                                  <div className={styles.dilekceTargetInfo}>
                                    <span className={styles.dilekceTargetName}>{target.name}</span>
                                    <span className={styles.dilekceTargetRole}>{target.isTest ? 'Test Modu' : 'Yetkili'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Gönderen Bilgileri */}
                          <div className={styles.dilekceSection}>
                            <div className={styles.dilekceSectionTitle}>
                              <FaUser className={styles.dilekceSectionIcon} />
                              <span>BAŞVURU SAHİBİ BİLGİLERİ</span>
                            </div>
                            <div className={styles.dilekceSenderInfo}>
                              <div className={styles.dilekceSenderRow}>
                                <span className={styles.dilekceSenderLabel}>Ad Soyad:</span>
                                <span className={styles.dilekceSenderValue}>
                                  {userData.eName && userData.eSurname ? `${userData.eName} ${userData.eSurname}` : 'Yükleniyor...'}
                                </span>
                              </div>
                              <div className={styles.dilekceSenderRow}>
                                <span className={styles.dilekceSenderLabel}>E-posta:</span>
                                <span className={styles.dilekceSenderValue}>{userData.eMail || auth.currentUser?.email || ''}</span>
                              </div>
                              {userData.eTitle && (
                                <div className={styles.dilekceSenderRow}>
                                  <span className={styles.dilekceSenderLabel}>Unvan:</span>
                                  <span className={styles.dilekceSenderValue}>{userData.eTitle}</span>
                                </div>
                              )}
                              {userData.eDepartment && (
                                <div className={styles.dilekceSenderRow}>
                                  <span className={styles.dilekceSenderLabel}>Departman:</span>
                                  <span className={styles.dilekceSenderValue}>{userData.eDepartment}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Dilekçe Türü */}
                          <div className={styles.dilekceSection}>
                            <div className={styles.dilekceSectionTitle}>
                              <FaFileAlt className={styles.dilekceSectionIcon} />
                              <span>DİLEKÇE TÜRÜ</span>
                            </div>
                            <Form.Select
                              className={styles.dilekceSelect}
                              value={complaintType}
                              onChange={(e) => setComplaintType(e.target.value)}
                            >
                              <option value="">Dilekçe türünü seçiniz...</option>
                              {complaintTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>

                          {/* Konu */}
                          <div className={styles.dilekceSection}>
                            <div className={styles.dilekceSectionTitle}>
                              <FaEdit className={styles.dilekceSectionIcon} />
                              <span>KONU</span>
                            </div>
                            <Form.Control
                              type="text"
                              placeholder="Dilekçenizin konusunu kısaca yazınız..."
                              className={styles.dilekceInput}
                              value={complaintSubject}
                              onChange={(e) => setComplaintSubject(e.target.value)}
                            />
                          </div>

                          {/* İçerik */}
                          <div className={styles.dilekceSection}>
                            <div className={styles.dilekceSectionTitle}>
                              <FaFileAlt className={styles.dilekceSectionIcon} />
                              <span>DİLEKÇE İÇERİĞİ</span>
                            </div>
                            <div className={styles.dilekceContentWrapper}>
                              <p className={styles.dilekceGreeting}>Sayın İlgili,</p>
                              <Form.Control
                                as="textarea"
                                rows={8}
                                placeholder="Başvurunuzun detaylarını buraya yazınız. Tüm bilgileri açık ve net bir şekilde belirtiniz..."
                                className={styles.dilekceTextarea}
                                value={complaintDetails}
                                onChange={(e) => setComplaintDetails(e.target.value)}
                              />
                              <p className={styles.dilekceClosing}>
                                Gereğini bilgilerinize arz ederim.
                              </p>
                              <p className={styles.dilekceClosing}>
                                Saygılarımla,
                              </p>
                              <p className={styles.dilekceSignature}>
                                {userData.eName && userData.eSurname ? `${userData.eName} ${userData.eSurname}` : ''}
                              </p>
                            </div>
                          </div>

                          {/* Gizlilik Notu */}
                          <div className={styles.dilekcePrivacyNote}>
                            <FaLock className={styles.dilekcePrivacyIcon} />
                            <span>Bu dilekçe gizlilikle değerlendirilecek ve yalnızca yetkili kişiler tarafından incelenecektir.</span>
                          </div>

                          {/* Gönder Butonu */}
                          <Button
                            className={styles.dilekceSubmitBtn}
                            onClick={handleComplaintSubmit}
                            disabled={complaintLoading}
                          >
                            {complaintLoading ? (
                              <>
                                <FaSpinner className="spinner-border-sm me-2" />
                                Dilekçe Gönderiliyor...
                              </>
                            ) : (
                              <>
                                <FaCheckCircle className="me-2" />
                                Dilekçeyi Gönder
                              </>
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Geçmiş Dilekçelerim */}
                      <div className={`${styles.dilekceHistoryContainer} mt-4`}>
                        <h3 className={styles.dilekceHistoryTitle}>
                          <FaFileAlt className="me-2" />
                          Geçmiş Dilekçelerim
                        </h3>
                        <p className={styles.dilekceHistorySubtitle}>
                          Daha önce gönderdiğiniz dilekçelerin durumunu buradan takip edebilirsiniz.
                        </p>

                        {complaintsLoading ? (
                          <div className="text-center py-4">
                            <FaSpinner className="spinner-border text-primary" />
                          </div>
                        ) : myComplaints.length === 0 ? (
                          <div className={styles.dilekceEmptyState}>
                            <FaFileAlt className={styles.dilekceEmptyIcon} />
                            <p>Henüz bir dilekçe kaydınız bulunmamaktadır.</p>
                          </div>
                        ) : (
                          <div className={styles.dilekceHistoryList}>
                            {myComplaints.map((complaint) => (
                              <div key={complaint.id} className={styles.dilekceHistoryItem}>
                                <div className={styles.dilekceHistoryItemHeader}>
                                  <div className={styles.dilekceHistoryItemLeft}>
                                    <span className={styles.dilekceHistoryDocNum}>
                                      {complaint.documentNumber || 'DLK-XXXXX'}
                                    </span>
                                    <h5 className={styles.dilekceHistorySubject}>{complaint.subject}</h5>
                                    <div className={styles.dilekceHistoryMeta}>
                                      <span className={styles.dilekceHistoryType}>
                                        {complaint.typeLabel || complaint.type}
                                      </span>
                                      {complaint.targetNames && complaint.targetNames.length > 0 && (
                                        <span className={styles.dilekceHistoryTarget}>
                                          Muhatap: {complaint.targetNames.join(', ')}
                                        </span>
                                      )}
                                    </div>
                                    <p className={styles.dilekceHistoryDate}>
                                      {complaint.createdAt?.toDate ? formatDate(complaint.createdAt.toDate()) : formatDate(new Date(complaint.createdAt))}
                                    </p>
                                  </div>
                                  <div className={styles.dilekceHistoryItemRight}>
                                    <span className={`${styles.dilekceStatusBadge} ${
                                      complaint.status === 'PENDING' ? styles.dilekceStatusPending :
                                      complaint.status === 'INCELENIYOR' ? styles.dilekceStatusReview :
                                      complaint.status === 'RESOLVED' ? styles.dilekceStatusResolved :
                                      styles.dilekceStatusClosed
                                    }`}>
                                      {complaint.status === 'PENDING' ? 'Beklemede' :
                                       complaint.status === 'INCELENIYOR' ? 'İnceleniyor' :
                                       complaint.status === 'RESOLVED' ? 'Çözüldü' : 'Kapatıldı'}
                                    </span>
                                  </div>
                                </div>
                                <div className={styles.dilekceHistoryContent}>
                                  <p>{complaint.details}</p>
                                </div>

                                {/* Show Replies from Yetkili */}
                                {complaint.replies && complaint.replies.length > 0 && (
                                  <div className={styles.dilekceRepliesContainer}>
                                    <div className={styles.dilekceRepliesTitle}>
                                      <FaComments className="me-2" />
                                      Yetkili Yanıtları ({complaint.replies.length})
                                    </div>
                                    <div className={styles.dilekceRepliesList}>
                                      {complaint.replies.map((reply, index) => (
                                        <div key={index} className={styles.dilekceReplyItem}>
                                          <div className={styles.dilekceReplyHeader}>
                                            <span className={styles.dilekceReplyAuthor}>
                                              <FaUserTie className="me-1" />
                                              {reply.repliedBy}
                                            </span>
                                            <span className={styles.dilekceReplyDate}>
                                              {reply.repliedAt?.toDate
                                                ? formatDate(reply.repliedAt.toDate())
                                                : formatDate(new Date(reply.repliedAt))}
                                            </span>
                                          </div>
                                          <div className={styles.dilekceReplyText}>{reply.text}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'gelen-sikayetler' && (isUserAdmin || isComplaintTarget) && (
                    <div className={styles.errorReportTabContent}>
                      <div className={styles.dilekceHistoryContainer}>
                        <h3 className={styles.dilekceHistoryTitle}>
                          <FaFileAlt className="me-2" />
                          Gelen Dilekçeler
                        </h3>
                        <p className={styles.dilekceHistorySubtitle}>
                          {isUserAdmin
                            ? 'Sisteme gelen tüm dilekçeleri buradan görüntüleyebilir ve yönetebilirsiniz.'
                            : 'Size iletilen dilekçeleri buradan görüntüleyebilirsiniz.'}
                        </p>

                        {allComplaintsLoading ? (
                          <div className="text-center py-4">
                            <FaSpinner className="spinner-border text-primary" />
                          </div>
                        ) : allComplaints.length === 0 ? (
                          <div className={styles.dilekceEmptyState}>
                            <FaFileAlt className={styles.dilekceEmptyIcon} />
                            <p>Henüz bir dilekçe kaydı bulunmamaktadır.</p>
                          </div>
                        ) : (
                          <div className={styles.dilekceHistoryList}>
                            {allComplaints.map((complaint) => (
                              <div
                                key={complaint.id}
                                className={`${styles.dilekceHistoryItem} ${styles.dilekceHistoryItemClickable}`}
                              >
                                <div className={styles.dilekceHistoryItemHeader} onClick={() => handleComplaintClick(complaint)}>
                                  <div className={styles.dilekceHistoryItemLeft}>
                                    <span className={styles.dilekceHistoryDocNum}>
                                      {complaint.documentNumber || 'DLK-XXXXX'}
                                    </span>
                                    <h5 className={styles.dilekceHistorySubject}>{complaint.subject}</h5>
                                    <div className={styles.dilekceHistorySender}>
                                      <FaUser className="me-1" />
                                      <strong>{complaint.employeeName}</strong> ({complaint.employeeEmail})
                                    </div>
                                    <div className={styles.dilekceHistoryMeta}>
                                      <span className={styles.dilekceHistoryType}>
                                        {complaint.typeLabel || complaint.type}
                                      </span>
                                      {complaint.targetNames && complaint.targetNames.length > 0 && (
                                        <span className={styles.dilekceHistoryTarget}>
                                          Muhatap: {complaint.targetNames.join(', ')}
                                        </span>
                                      )}
                                    </div>
                                    <p className={styles.dilekceHistoryDate}>
                                      {complaint.createdAt?.toDate ? formatDate(complaint.createdAt.toDate()) : formatDate(new Date(complaint.createdAt))}
                                    </p>
                                  </div>
                                  <div className={styles.dilekceHistoryItemRight}>
                                    <span className={`${styles.dilekceStatusBadge} ${
                                      complaint.status === 'PENDING' ? styles.dilekceStatusPending :
                                      complaint.status === 'INCELENIYOR' ? styles.dilekceStatusReview :
                                      complaint.status === 'RESOLVED' ? styles.dilekceStatusResolved :
                                      styles.dilekceStatusClosed
                                    }`}>
                                      {complaint.status === 'PENDING' ? 'Beklemede' :
                                       complaint.status === 'INCELENIYOR' ? 'İnceleniyor' :
                                       complaint.status === 'RESOLVED' ? 'Çözüldü' : 'Kapatıldı'}
                                    </span>
                                    <span className={styles.dilekceClickHint}>Detaylar için tıklayın</span>
                                  </div>
                                </div>
                                {/* Download Button */}
                                {complaint.docxUrl && (
                                  <div className={styles.dilekceDownloadSection}>
                                    <a
                                      href={complaint.docxUrl}
                                      download={complaint.docxFileName || `dilekce_${complaint.documentNumber}.docx`}
                                      className={styles.dilekceDownloadBtn}
                                      onClick={(e) => e.stopPropagation()}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <FaFileWord className="me-2" />
                                      Dilekçeyi İndir (DOCX)
                                    </a>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'masraf-talep' && (
                    <div className={styles.expenseFormTab}>
                      {expenseSubmitted ? (
                        <div className={styles.expenseSuccessState}>
                          <div className={styles.expenseSuccessIcon}>
                            <FaCheckCircle />
                          </div>
                          <h3 className={styles.expenseSuccessTitle}>Masraf Talebiniz İletildi</h3>
                          <p className={styles.expenseSuccessText}>
                            Talebiniz başarıyla oluşturuldu ve onay için ilgili kişilere iletildi.
                          </p>
                          <button
                            className={styles.expenseNewRequestBtn}
                            onClick={() => {
                              setExpenseSubmitted(false);
                              setExpensePdfFiles([]);
                              // Input'u temizle
                              const fileInput = document.getElementById('expense-pdf-input');
                              if (fileInput) fileInput.value = '';
                            }}
                          >
                            <FaRegMoneyBillAlt />
                            Yeni Talep Oluştur
                          </button>
                        </div>
                      ) : (
                        <>
                      <div className={styles.expenseFormHeader}>
                        <div className={styles.expenseFormIconWrapper}>
                          <FaRegMoneyBillAlt />
                        </div>
                        <div>
                          <h3 className={styles.expenseFormTitle}>Masraf Talep Formu</h3>
                          <p className={styles.expenseFormSubtitle}>
                            Masraf talebinizi aşağıdaki form ile oluşturabilirsiniz. Talebiniz onay için ilgili kişilere iletilecektir.
                          </p>
                        </div>
                      </div>

                      <div className={styles.expenseFormBody}>
                        <Form className={styles.expenseFormGrid}>
                          <div className={styles.expenseFormRow}>
                            {/* Tutar */}
                            <Form.Group className={styles.expenseFormGroup}>
                              <Form.Label className={styles.expenseLabel}>
                                Tutar (₺) <span className={styles.requiredStar}>*</span>
                              </Form.Label>
                              <div className={styles.expenseInputWrapper}>
                                <span className={styles.expenseInputPrefix}>₺</span>
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={expenseAmount}
                                  onChange={(e) => { setExpenseAmount(e.target.value); setExpenseErrors(prev => ({ ...prev, amount: undefined })); }}
                                  placeholder="0.00"
                                  className={`${styles.expenseInput} ${expenseErrors.amount ? styles.expenseInputError : ''}`}
                                  required
                                />
                              </div>
                              {expenseErrors.amount && <span className={styles.expenseFieldError}>{expenseErrors.amount}</span>}
                            </Form.Group>

                            {/* Kategori */}
                            <Form.Group className={styles.expenseFormGroup}>
                              <Form.Label className={styles.expenseLabel}>
                                Kategori <span className={styles.requiredStar}>*</span>
                              </Form.Label>
                              <Form.Select
                                value={expenseCategory}
                                onChange={(e) => setExpenseCategory(e.target.value)}
                                className={styles.expenseSelect}
                              >
                                {expenseCategories.map(category => (
                                  <option key={category} value={category}>
                                    {category}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </div>

                          {/* Açıklama */}
                          <Form.Group className={styles.expenseFormGroup}>
                            <Form.Label className={styles.expenseLabel}>
                              Açıklama <span className={styles.requiredStar}>*</span>
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              value={expenseDescription}
                              onChange={(e) => { setExpenseDescription(e.target.value); setExpenseErrors(prev => ({ ...prev, description: undefined })); }}
                              placeholder="Masraf talebinizin detaylarını açıklayın..."
                              className={`${styles.expenseTextarea} ${expenseErrors.description ? styles.expenseInputError : ''}`}
                              required
                            />
                            {expenseErrors.description && <span className={styles.expenseFieldError}>{expenseErrors.description}</span>}
                          </Form.Group>

                          {/* PDF Yükleme */}
                          <Form.Group className={styles.expenseFormGroup}>
                            <Form.Label className={styles.expenseLabel}>
                              Belgeler Yükle (PDF) <span className={styles.optionalText}>(İsteğe bağlı)</span>
                            </Form.Label>
                            <div className={styles.pdfUploadContainer}>
                              <div className={styles.pdfUploadArea}>
                                <input
                                  type="file"
                                  id="expense-pdf-input"
                                  accept=".pdf"
                                  multiple
                                  onChange={(e) => {
                                    console.log('🎯 File input changed, multiple attribute:', e.target.multiple);
                                    handlePdfFileSelect(e);
                                  }}
                                  className={styles.pdfFileInput}
                                />
                                <label htmlFor="expense-pdf-input" className={styles.pdfUploadLabel}>
                                  <FaFilePdf className={styles.pdfUploadIcon} />
                                  <span className={styles.pdfUploadText}>PDF Dosyaları Seç</span>
                                  <span className={styles.pdfUploadSubtext}>Birden fazla dosya seçebilirsiniz (Her biri max 10MB)</span>
                                </label>
                              </div>
                              
                              {expensePdfFiles.length > 0 && (
                                <div className={styles.selectedFilesContainer}>
                                  <div className={styles.selectedFilesHeader}>
                                    <span className={styles.selectedFilesTitle}>
                                      Seçilen Dosyalar ({expensePdfFiles.length})
                                    </span>
                                    <button
                                      type="button"
                                      className={styles.removeAllBtn}
                                      onClick={handleRemoveAllPdfs}
                                      title="Tüm Dosyaları Kaldır"
                                    >
                                      Tümünü Kaldır
                                    </button>
                                  </div>
                                  <div className={styles.selectedFilesList}>
                                    {expensePdfFiles.map((file, index) => (
                                      <div key={index} className={styles.pdfSelectedContainer}>
                                        <div className={styles.pdfSelectedInfo}>
                                          <FaFilePdf className={styles.pdfSelectedIcon} />
                                          <div className={styles.pdfSelectedDetails}>
                                            <span className={styles.pdfSelectedName}>{file.name}</span>
                                            <span className={styles.pdfSelectedSize}>
                                              {(file.size / 1024 / 1024).toFixed(2)} MB
                                            </span>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          className={styles.pdfRemoveBtn}
                                          onClick={() => handleRemovePdf(index)}
                                          title="Dosyayı Kaldır"
                                        >
                                          <FaTimes />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Form.Group>

                          {/* Submit */}
                          <div className={styles.expenseFormFooter}>
                            <Button 
                              className={styles.expenseSubmitBtn} 
                              onClick={handleExpenseRequestSubmit}
                              disabled={expenseLoading || expensePdfUploading}
                            >
                              {expenseLoading || expensePdfUploading ? (
                                <>
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  {expensePdfUploading ? 'PDF Yükleniyor...' : 'Gönderiliyor...'}
                                </>
                              ) : (
                                <>
                                  <FaCheckCircle className={styles.buttonIcon} />
                                  Masraf Talebini Gönder
                                </>
                              )}
                            </Button>
                          </div>
                        </Form>
                      </div>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'masraf-takip' && (
                    <div className={styles.expenseTrackingTab}>
                      <div className={styles.trackingHeader}>
                        <div className={styles.trackingHeaderIcon}><FaRegMoneyBillAlt /></div>
                        <div>
                          <h3 className={styles.trackingHeaderTitle}>Masraf Taleplerim</h3>
                          <p className={styles.trackingHeaderSubtitle}>Gönderdiğiniz masraf taleplerinin durumunu takip edin</p>
                        </div>
                      </div>

                      {expenseTrackingLoading ? (
                        <div className={styles.trackingLoading}>
                          <FaSpinner className={styles.trackingSpinner} />
                          <span>Talepleriniz yükleniyor...</span>
                        </div>
                      ) : myExpenseRequests.length === 0 ? (
                        <div className={styles.trackingEmpty}>
                          <div className={styles.trackingEmptyIcon}><FaRegMoneyBillAlt /></div>
                          <p className={styles.trackingEmptyTitle}>Henüz masraf talebiniz yok</p>
                          <p className={styles.trackingEmptyText}>Masraf Talep Et sekmesinden yeni bir talep oluşturabilirsiniz.</p>
                          <button className={styles.trackingEmptyBtn} onClick={() => setActiveTab('masraf-talep')}>Talep Oluştur</button>
                        </div>
                      ) : (
                        <>
                          <div className={styles.trackingSummary}>
                            <div className={`${styles.trackingStat} ${styles.trackingStatTotal}`}>
                              <span className={styles.trackingStatValue}>{myExpenseRequests.length}</span>
                              <span className={styles.trackingStatLabel}>Toplam</span>
                            </div>
                            <div className={`${styles.trackingStat} ${styles.trackingStatPending}`}>
                              <span className={styles.trackingStatValue}>{myExpenseRequests.filter(r => r.status === 'pending').length}</span>
                              <span className={styles.trackingStatLabel}>Bekliyor</span>
                            </div>
                            <div className={`${styles.trackingStat} ${styles.trackingStatApproved}`}>
                              <span className={styles.trackingStatValue}>{myExpenseRequests.filter(r => r.status === 'approved').length}</span>
                              <span className={styles.trackingStatLabel}>Onaylandı</span>
                            </div>
                            <div className={`${styles.trackingStat} ${styles.trackingStatRejected}`}>
                              <span className={styles.trackingStatValue}>{myExpenseRequests.filter(r => r.status === 'rejected').length}</span>
                              <span className={styles.trackingStatLabel}>Reddedildi</span>
                            </div>
                          </div>

                          <div className={styles.trackingList}>
                            {myExpenseRequests.map((req) => (
                              <motion.div
                                key={req.id}
                                className={`${styles.trackingCard} ${styles[`trackingCard_${req.status}`]}`}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                              >
                                <div className={styles.trackingCardHeader}>
                                  <span className={styles.trackingAmount}>₺{(req.amount || 0).toLocaleString('tr-TR')}</span>
                                  <span className={`${styles.trackingBadge} ${styles[`trackingBadge_${req.status}`]}`}>
                                    {req.status === 'approved' && <><FaCheckCircle /> Onaylandı</>}
                                    {req.status === 'rejected' && <><FaTimesCircle /> Reddedildi</>}
                                    {req.status === 'pending' && <><FaClock /> Bekliyor</>}
                                  </span>
                                </div>
                                <div className={styles.trackingCardBody}>
                                  <div className={styles.trackingMeta}>
                                    <span className={styles.trackingCategory}><FaTag /> {req.category || 'Diğer'}</span>
                                    <span className={styles.trackingDate}><FaCalendarAlt /> {req.createdAt ? new Date(req.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</span>
                                  </div>
                                  {req.description && <p className={styles.trackingDesc}>{req.description}</p>}
                                  
                                  {/* Edit Button - Only for pending status */}
                                  {req.status === 'pending' && (
                                    <div className={styles.trackingEditSection}>
                                      <button 
                                        className={styles.trackingEditBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          console.log('📝 Düzenle butonuna tıklandı:', req);
                                          setEditingExpense(req);
                                          setEditExpenseAmount(req.amount || '');
                                          setEditExpenseCategory(req.category || 'Diğer');
                                          setEditExpenseDescription(req.description || '');
                                          setEditExpensePdfFiles([]);
                                          // pdfUrls array veya pdfUrl string kontrolü
                                          const hasPdf = (req.pdfUrls && Array.isArray(req.pdfUrls) && req.pdfUrls.length > 0) || !!req.pdfUrl;
                                          setKeepExistingPdf(hasPdf);
                                          console.log('📎 Has existing PDF:', hasPdf);
                                          setShowEditExpenseModal(true);
                                        }}
                                      >
                                        <FaEdit /> Düzenle
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Rejection Reason */}
                                  {req.status === 'rejected' && req.rejectionReason && (
                                    <div className={styles.trackingRejectionSection}>
                                      <button 
                                        className={styles.trackingViewReasonBtn}
                                        onClick={() => {
                                          setSelectedReason(req.rejectionReason);
                                          setShowReasonModal(true);
                                        }}
                                      >
                                        <FaInfoCircle /> Nedeni Gör
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {req.status !== 'pending' && req.updatedAt && (
                                  <div className={styles.trackingCardFooter}>
                                    <span className={styles.trackingProcessed}>
                                      İşlem tarihi: {new Date(req.updatedAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                )}
                              </motion.div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'masraf-talepleri' && isUserAdmin && (
                    <div className={styles.expenseRequestsTabContent}>
                      <ExpenseRequests />
                    </div>
                  )}
                </div>

                {/* Rejection Reason Modal */}
                {showReasonModal && (
                  <div className={styles.modalOverlay} onClick={() => setShowReasonModal(false)}>
                    <div className={styles.modalContainer} onClick={e => e.stopPropagation()}>
                      <button className={styles.modalCloseButton} onClick={() => setShowReasonModal(false)}>
                        <FaTimes />
                      </button>
                      <div className={styles.modalBody}>
                        <h3 style={{ marginBottom: '16px', fontSize: '1.25rem', fontWeight: 600 }}>Reddedilme Nedeni</h3>
                        <p className={styles.rejectionReasonText}>{selectedReason}</p>
                      </div>
                      <div className={styles.modalFooter}>
                        <button 
                          className={styles.modalCancelButton} 
                          onClick={() => setShowReasonModal(false)}
                        >
                          Kapat
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Expense Modal */}
                {showEditExpenseModal && editingExpense && (
                  <div className={styles.modalOverlay} onClick={() => setShowEditExpenseModal(false)}>
                    <div className={styles.modalContainer} onClick={e => e.stopPropagation()}>
                      <button className={styles.modalCloseButton} onClick={() => setShowEditExpenseModal(false)}>
                        <FaTimes />
                      </button>
                      <div className={styles.modalBody}>
                        <h3 style={{ marginBottom: '24px', fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaEdit /> Masraf Talebini Düzenle
                        </h3>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          {/* Tutar */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '0.9rem' }}>
                              Tutar (₺) <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editExpenseAmount}
                              onChange={(e) => setEditExpenseAmount(e.target.value)}
                              placeholder="Örn: 150.00"
                              style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.95rem'
                              }}
                            />
                          </div>

                          {/* Kategori */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '0.9rem' }}>
                              Kategori <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <select
                              value={editExpenseCategory}
                              onChange={(e) => setEditExpenseCategory(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.95rem'
                              }}
                            >
                              {expenseCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>

                          {/* Açıklama */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '0.9rem' }}>
                              Açıklama <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <textarea
                              value={editExpenseDescription}
                              onChange={(e) => setEditExpenseDescription(e.target.value)}
                              placeholder="Masraf detaylarını açıklayınız..."
                              rows={4}
                              style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                resize: 'vertical'
                              }}
                            />
                          </div>

                          {/* Mevcut PDF Bilgisi */}
                          {keepExistingPdf && editExpensePdfFiles.length === 0 && (
                            <>
                              {/* pdfUrls array formatı */}
                              {editingExpense.pdfUrls && Array.isArray(editingExpense.pdfUrls) && editingExpense.pdfUrls.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {editingExpense.pdfUrls.map((url, index) => (
                                    <div key={index} style={{
                                      padding: '12px 16px',
                                      background: '#f0fdf4',
                                      border: '1px solid #86efac',
                                      borderRadius: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: '12px'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                        <FaFilePdf style={{ color: '#22c55e', fontSize: '1.2rem', flexShrink: 0 }} />
                                        <span style={{ 
                                          fontSize: '0.9rem', 
                                          color: '#166534', 
                                          fontWeight: 500,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          {editingExpense.pdfFileNames?.[index] || `Mevcut Belge ${index + 1}.pdf`}
                                        </span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                        {/* Önizleme Butonu */}
                                        <button
                                          type="button"
                                          onClick={() => window.open(url, '_blank')}
                                          title="PDF'i Görüntüle"
                                          style={{
                                            padding: '6px 10px',
                                            background: '#dbeafe',
                                            color: '#1e40af',
                                            border: '1px solid #93c5fd',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#bfdbfe';
                                            e.currentTarget.style.borderColor = '#60a5fa';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = '#dbeafe';
                                            e.currentTarget.style.borderColor = '#93c5fd';
                                          }}
                                        >
                                          <FaEye />
                                        </button>
                                        
                                        {/* İndirme Butonu */}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = editingExpense.pdfFileNames?.[index] || `masraf-belgesi-${index + 1}.pdf`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            toast.success('PDF indiriliyor...');
                                          }}
                                          title="PDF'i İndir"
                                          style={{
                                            padding: '6px 10px',
                                            background: '#e0e7ff',
                                            color: '#4338ca',
                                            border: '1px solid #c7d2fe',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#c7d2fe';
                                            e.currentTarget.style.borderColor = '#a5b4fc';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = '#e0e7ff';
                                            e.currentTarget.style.borderColor = '#c7d2fe';
                                          }}
                                        >
                                          <FaDownload />
                                        </button>
                                        
                                        {/* Kaldır Butonu */}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setKeepExistingPdf(false);
                                            toast.success('Mevcut belgeler kaldırıldı');
                                          }}
                                          title="Belgeyi Kaldır"
                                          style={{
                                            padding: '6px 10px',
                                            background: '#fee2e2',
                                            color: '#dc2626',
                                            border: '1px solid #fecaca',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#fecaca';
                                            e.currentTarget.style.borderColor = '#f87171';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = '#fee2e2';
                                            e.currentTarget.style.borderColor = '#fecaca';
                                          }}
                                        >
                                          <FaTimes />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* pdfUrl string formatı (backward compatibility) */}
                              {editingExpense.pdfUrl && (!editingExpense.pdfUrls || !Array.isArray(editingExpense.pdfUrls) || editingExpense.pdfUrls.length === 0) && (
                                <div style={{
                                  padding: '12px 16px',
                                  background: '#f0fdf4',
                                  border: '1px solid #86efac',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '12px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                    <FaFilePdf style={{ color: '#22c55e', fontSize: '1.2rem', flexShrink: 0 }} />
                                    <span style={{ 
                                      fontSize: '0.9rem', 
                                      color: '#166534', 
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {editingExpense.pdfFileName || 'Mevcut Belge.pdf'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                    {/* Önizleme Butonu */}
                                    <button
                                      type="button"
                                      onClick={() => window.open(editingExpense.pdfUrl, '_blank')}
                                      title="PDF'i Görüntüle"
                                      style={{
                                        padding: '6px 10px',
                                        background: '#dbeafe',
                                        color: '#1e40af',
                                        border: '1px solid #93c5fd',
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#bfdbfe';
                                        e.currentTarget.style.borderColor = '#60a5fa';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#dbeafe';
                                        e.currentTarget.style.borderColor = '#93c5fd';
                                      }}
                                    >
                                      <FaEye />
                                    </button>
                                    
                                    {/* İndirme Butonu */}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = editingExpense.pdfUrl;
                                        link.download = editingExpense.pdfFileName || 'masraf-belgesi.pdf';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        toast.success('PDF indiriliyor...');
                                      }}
                                      title="PDF'i İndir"
                                      style={{
                                        padding: '6px 10px',
                                        background: '#e0e7ff',
                                        color: '#4338ca',
                                        border: '1px solid #c7d2fe',
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#c7d2fe';
                                        e.currentTarget.style.borderColor = '#a5b4fc';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#e0e7ff';
                                        e.currentTarget.style.borderColor = '#c7d2fe';
                                      }}
                                    >
                                      <FaDownload />
                                    </button>
                                    
                                    {/* Kaldır Butonu */}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setKeepExistingPdf(false);
                                        toast.success('Mevcut belge kaldırıldı');
                                      }}
                                      title="Belgeyi Kaldır"
                                      style={{
                                        padding: '6px 10px',
                                        background: '#fee2e2',
                                        color: '#dc2626',
                                        border: '1px solid #fecaca',
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#fecaca';
                                        e.currentTarget.style.borderColor = '#f87171';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#fee2e2';
                                        e.currentTarget.style.borderColor = '#fecaca';
                                      }}
                                    >
                                      <FaTimes />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* Yeni PDF Yükleme */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '0.9rem' }}>
                              {keepExistingPdf && (editingExpense.pdfUrl || (editingExpense.pdfUrls && editingExpense.pdfUrls.length > 0)) ? 'Belgeyi Değiştir' : 'PDF Belgesi Ekle'}
                            </label>
                            
                            {/* Yeni seçilen dosyalar */}
                            {editExpensePdfFiles.length > 0 && (
                              <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {editExpensePdfFiles.map((file, index) => (
                                  <div key={index} style={{
                                    padding: '10px 12px',
                                    background: '#dbeafe',
                                    border: '1px solid #93c5fd',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <FaFilePdf style={{ color: '#2563eb', fontSize: '1.1rem' }} />
                                      <span style={{ fontSize: '0.85rem', color: '#1e40af' }}>{file.name}</span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveEditPdf(index)}
                                      style={{
                                        padding: '4px 8px',
                                        background: '#fee2e2',
                                        color: '#dc2626',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      <FaTimes />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <label
                              htmlFor="edit-expense-pdf-input"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 18px',
                                background: 'white',
                                color: '#2563eb',
                                border: '2px dashed #93c5fd',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#eff6ff';
                                e.currentTarget.style.borderColor = '#2563eb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.borderColor = '#93c5fd';
                              }}
                            >
                              <FaUpload /> PDF Seç
                            </label>
                            <input
                              id="edit-expense-pdf-input"
                              type="file"
                              accept="application/pdf"
                              multiple
                              onChange={handleEditExpensePdfChange}
                              style={{ display: 'none' }}
                            />
                            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                              Maksimum dosya boyutu: 10MB. Birden fazla PDF seçebilirsiniz.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className={styles.modalFooter}>
                        <button 
                          className={styles.modalCancelButton} 
                          onClick={() => setShowEditExpenseModal(false)}
                          disabled={editExpenseLoading}
                        >
                          İptal
                        </button>
                        <button 
                          className={styles.modalPrimaryButton}
                          onClick={handleExpenseUpdate}
                          disabled={editExpenseLoading}
                        >
                          {editExpenseLoading ? (
                            <>
                              <FaSpinner className="fa-spin" />
                              Güncelleniyor...
                            </>
                          ) : (
                            <>
                              <FaSave />
                              Kaydet
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              </div>

                {/* Summary Information Section - Now placed inside the right column */}
                <div className={styles.summarySection}>
                  <h2 className={styles.summarySectionTitle}>
                    <motion.span
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      Özet Bilgiler
                    </motion.span>
                  </h2>
                  
                  <div className={styles.statsGrid}>
                    {/* Project Stats Card */}
                    <motion.div 
                      className={styles.statCard}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1, duration: 0.5 }}
                      whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                    >
                      <div className={`${styles.statIconContainer} ${styles.projectIconBg}`}>
                        <FaProjectDiagram size={24} />
                      </div>
                      <div className={styles.statInfo}>
                        <h3 className={styles.statTitle}>Aktif Projeler</h3>
                        <div className={styles.statValueContainer}>
                          <span className={styles.statValue}>
                            <AnimatedCounter from={0} to={activeProjectCount} duration={1.5} />
                          </span>
                        </div>
                        <div className={styles.statProgress}>
                          <div 
                            className={styles.statProgressBar} 
                            style={{ width: `${Math.min(activeProjectCount * 10, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </motion.div>

                    {/* SOP Document Stats Card */}
                    <motion.div 
                      className={styles.statCard}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                      whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                    >
                      <div className={`${styles.statIconContainer} ${styles.documentIconBg}`}>
                        <FaFileAlt size={24} />
                      </div>
                      <div className={styles.statInfo}>
                        <h3 className={styles.statTitle}>SOP Dokümanları</h3>
                        <div className={styles.statValueContainer}>
                          <span className={styles.statValue}>
                            <AnimatedCounter from={0} to={mainSOPList.length} duration={1.5} />
                          </span>
                        </div>
                        <div className={styles.statProgress}>
                          <div 
                            className={styles.statProgressBar} 
                            style={{ width: `${Math.min((mainSOPList.length / 20) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Viewed SOPs Stats Card */}
                    <motion.div 
                      className={styles.statCard}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                    >
                      <div className={`${styles.statIconContainer} ${styles.eyeIconBg}`}>
                        <FaEye size={24} />
                      </div>
                      <div className={styles.statInfo}>
                        <h3 className={styles.statTitle}>Görüntülenen SOPlar</h3>
                        <div className={styles.statValueContainer}>
                          <span className={styles.statValue}>
                            <AnimatedCounter from={0} to={viewedSOPs.size} duration={1.5} />
                          </span>
                          <span className={styles.statBadge}>
                            {mainSOPList.length > 0 
                              ? `${Math.round((viewedSOPs.size / mainSOPList.length) * 100)}%` 
                              : '0%'}
                          </span>
                        </div>
                        <div className={styles.statProgress}>
                          <div 
                            className={styles.statProgressBar} 
                            style={{ width: `${mainSOPList.length > 0 ? (viewedSOPs.size / mainSOPList.length) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Experience Level Stats Card */}
                    <motion.div 
                      className={styles.statCard}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                      whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                    >
                      <div className={`${styles.statIconContainer} ${styles.userIconBg}`}>
                        <FaUserTie size={24} />
                      </div>
                      <div className={styles.statInfo}>
                        <h3 className={styles.statTitle}>Tecrübe Seviyesi</h3>
                        <div className={styles.statValueContainer}>
                          <div className={styles.experienceLevelContainer}>
                            {[1, 2, 3, 4, 5].map((level) => (
                              <motion.div 
                                key={level}
                                className={`${styles.experienceDot} ${level <= 3 ? styles.activeDot : ''}`}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.4 + (level * 0.1) }}
                              />
                            ))}
                          </div>
                          <span className={styles.statBadge}>Orta</span>
                        </div>
                        <div className={styles.statMetaInfo}>Son 6 ayda {viewedSOPs.size} SOP görüntülendi</div>
                      </div>
                    </motion.div>
                                   </div>

                  <div className={styles.additionalStatsRow}>
                    <motion.div 
                      className={styles.activityTimelineCard}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      <h3 className={styles.cardSubtitle}>Sistem Aktivite Özeti</h3>
                      <div className={styles.timelineContainer}>
                        <div className={styles.timelineItem}>
                          <div className={styles.timelineDot}></div>
                          <div className={styles.timelineContent}>
                            <span className={styles.timelineDate}>Bugün</span>
                            <p className={styles.timelineText}>
                              <strong>{Math.max(1, Math.floor(Math.random() * 5))}</strong> SOP dokümanı görüntülendi
                            </p>
                          </div>
                        </div>
                        <div className={styles.timelineItem}>
                          <div className={styles.timelineDot}></div>
                          <div className={styles.timelineContent}>
                            <span className={styles.timelineDate}>Son Hafta</span>
                            <p className={styles.timelineText}>
                              <strong>{Math.max(2, Math.floor(Math.random() * 8))}</strong> Proje ziyareti gerçekleşti
                            </p>
                          </div>
                        </div>
                        <div className={styles.timelineItem}>
                          <div className={styles.timelineDot}></div>
                          <div className={styles.timelineContent}>
                            <span className={styles.timelineDate}>Son Ay</span>
                            <p className={styles.timelineText}>
                              <strong>{Math.max(5, Math.floor(Math.random() * 15))}</strong> Toplam sistem aktivitesi
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    <motion.div 
                      className={styles.completionCard}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                    >
                      <h3 className={styles.cardSubtitle}>Profil Tamamlanma Oranı</h3>
                      <div className={styles.completionContainer}>
                        <div className={styles.completionChart}>
                          <svg viewBox="0 0 36 36" className={styles.completionCircle}>
                            <path
                              className={styles.completionCircleBg}
                              d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                              className={styles.completionCircleProgress}
                              strokeDasharray={`${profileCompletion.overall}, 100`}
                              d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <text x="18" y="20.35" className={styles.completionPercentage}>
                              {profileCompletion.overall}%
                            </text>
                          </svg>
                        </div>
                        <div className={styles.completionDetails}>
                          <div className={styles.completionItem} title={profileCompletion.personal.detail}>
                            <span className={styles.completionLabel}>Kişisel Bilgiler</span>
                            <span className={styles.completionValue}>
                              {profileCompletion.personal.label} ({profileCompletion.personal.percent}%)
                            </span>
                            <div className={styles.completionProgress}>
                              <div
                                className={styles.completionProgressBar}
                                style={{ width: `${profileCompletion.personal.percent}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className={styles.completionItem}>
                            <span className={styles.completionLabel}>Profil Fotoğrafı</span>
                            <span className={styles.completionValue}>{profileCompletion.photo.label}</span>
                            <div className={styles.completionProgress}>
                              <div
                                className={styles.completionProgressBar}
                                style={{ width: `${profileCompletion.photo.percent}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className={styles.completionItem} title={profileCompletion.education.detail}>
                            <span className={styles.completionLabel}>Eğitim Tamamlama</span>
                            <span className={styles.completionValue}>
                              {profileCompletion.education.label} ({profileCompletion.education.percent}%)
                            </span>
                            <div className={styles.completionProgress}>
                              <div
                                className={styles.completionProgressBar}
                                style={{ width: `${profileCompletion.education.percent}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
            </div>
          </Col>
        </Row>

        {/* SOP Viewer Modal */}
        <Modal
          show={showSOPModal}
          onHide={closeSOPModal}
          size="lg"
          centered
          className={styles.modal}
        >
          <Modal.Header closeButton className={styles.modalHeader}>
            <Modal.Title className={styles.modalTitle}>
              {selectedSOP?.fileType === 'application/pdf' ? <FaFilePdf /> :
               selectedSOP?.fileType === 'video/mp4' ? <FaVideo /> :
               selectedSOP?.fileType === 'application/vnd.ms-powerpoint' || selectedSOP?.fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ? <FaFilePowerpoint /> :
               <FaFileAlt />}
              SOP Doküman Görüntüleyici
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            className={styles.modalBody}
            onScroll={handleSOPScroll}
          >
            {selectedSOP && (
              <div className={styles.sopModalContent}>
                {/* Document Info Section */}
                <div className={styles.sopDocumentInfo}>
                  <div className={styles.sopDocumentIcon}>
                    {selectedSOP.fileType === 'application/pdf' ? <FaFilePdf /> :
                     selectedSOP.fileType === 'video/mp4' ? <FaVideo /> :
                     selectedSOP.fileType === 'application/vnd.ms-powerpoint' || selectedSOP.fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ? <FaFilePowerpoint /> :
                     <FaFileAlt />}
                  </div>
                  <div className={styles.sopDocumentDetails}>
                    <h4 className={styles.sopDocumentTitle}>{selectedSOP.name}</h4>
                    <div className={styles.sopDocumentMeta}>
                      <span><FaTag /> Versiyon {selectedSOP.versionNumber}</span>
                      <span><FaClock /> {
                        selectedSOP.fileType === 'video/mp4' ? 'Video' :
                        selectedSOP.fileType === 'application/vnd.ms-powerpoint' || selectedSOP.fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ? 'PowerPoint Sunusu' :
                        'PDF Doküman'
                      }</span>
                    </div>
                  </div>
                  {readSOPs.has(selectedSOP.id) && (
                    <span className={styles.readBadge}>
                      <FaCheckCircle /> Okundu
                    </span>
                  )}
                </div>

                {/* Document Viewer */}
                {selectedSOP.fileType === 'application/pdf' ? (
                  <div className={styles.sopViewerContainer}>
                    <div className={styles.pdfContainer} onScroll={handleSOPScroll}>
                      <iframe
                        src={`${selectedSOP.fileUrl}#toolbar=0&navpanes=0`}
                        title={selectedSOP.name}
                        width="100%"
                        height="550px"
                        className={styles.pdfFrame}
                        onLoad={() => {
                          setTimeout(() => setHasScrolledToBottom(true), 3000);
                        }}
                      />
                    </div>
                  </div>
                ) : selectedSOP.fileType === 'video/mp4' ? (
                  <div className={styles.sopViewerContainer}>
                    <div className={styles.videoContainer}>
                      <video
                        controls
                        width="100%"
                        height="auto"
                        className={styles.videoPlayer}
                        onEnded={() => setHasScrolledToBottom(true)}
                      >
                        <source src={selectedSOP.fileUrl} type="video/mp4" />
                        Tarayıcınız video oynatmayı desteklemiyor.
                      </video>
                    </div>
                  </div>
                ) : selectedSOP.fileType === 'application/vnd.ms-powerpoint' || selectedSOP.fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ? (
                  <div className={styles.sopViewerContainer}>
                    <div className={styles.pdfContainer} onScroll={handleSOPScroll}>
                      <iframe
                        src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedSOP.fileUrl)}&embedded=true`}
                        title={selectedSOP.name}
                        width="100%"
                        height="550px"
                        className={styles.pdfFrame}
                        onLoad={() => {
                          setTimeout(() => setHasScrolledToBottom(true), 3000);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={styles.unsupportedFile}>
                    <FaFileAlt style={{ fontSize: '3rem', color: 'var(--text-tertiary)', marginBottom: '1rem' }} />
                    <p>Bu dosya türü görüntülenemiyor.</p>
                    <a href={selectedSOP.fileUrl} target="_blank" rel="noopener noreferrer" className={styles.downloadLink}>
                      <FaDownload /> Dosyayı İndir
                    </a>
                  </div>
                )}

                {/* Okudum Checkbox Section */}
                <div className={`${styles.okudumSection} ${hasScrolledToBottom || readSOPs.has(selectedSOP?.id) ? styles.okudumEnabled : styles.okudumDisabled}`}>
                  {!hasScrolledToBottom && !readSOPs.has(selectedSOP?.id) && (
                    <p className={styles.okudumHint}>
                      <FaExclamationTriangle className={styles.okudumHintIcon} />
                      Onay kutusunu işaretleyebilmek için lütfen dokümanı sonuna kadar okuyunuz veya videoyu izleyiniz.
                    </p>
                  )}
                  <div
                    className={`${styles.okudumCheckboxContainer} ${!hasScrolledToBottom && !readSOPs.has(selectedSOP?.id) ? styles.okudumCheckboxDisabled : ''}`}
                    onClick={() => {
                      if (hasScrolledToBottom || readSOPs.has(selectedSOP?.id)) {
                        toggleSOPRead(selectedSOP?.id);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={readSOPs.has(selectedSOP?.id)}
                      onChange={() => {}}
                      disabled={!hasScrolledToBottom && !readSOPs.has(selectedSOP?.id)}
                      className={styles.okudumCheckbox}
                    />
                    <span className={styles.okudumLabel}>
                      Bu SOP dokümanını okudum ve anladım
                    </span>
                    {readSOPs.has(selectedSOP?.id) && <FaCheckCircle className={styles.okudumCheckIcon} />}
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <Button variant="secondary" onClick={closeSOPModal} className={styles.modalCancelButton}>
              <FaTimes className={styles.buttonIcon} />
              Kapat
            </Button>
            <Button
              as="a"
              href={selectedSOP?.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.modalPrimaryButton}
            >
              <FaExternalLinkAlt className={styles.buttonIcon} />
              Yeni Sekmede Aç
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Project Visits Modal */}
        {selectedProject && (
          <ProjectVisitsModal
            show={showVisitsModal}
            onHide={() => setShowVisitsModal(false)}
            project={selectedProject}
            employeeId={employeeId}
          />
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className={styles.modalBackdrop}>
            <motion.div 
              className={styles.passwordModal}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className={styles.passwordModalHeader}>
                <h3 className={styles.passwordModalTitle}>
                  <FaLock className={styles.passwordModalIcon} />
                  Şifre Değiştir
                </h3>
                <button 
                  className={styles.modalCloseButton}
                  onClick={() => setShowPasswordModal(false)}
                  aria-label="Close modal"
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className={styles.passwordModalBody}>
                <div className={styles.formGroup}>
                  <label htmlFor="currentPassword" className={styles.formLabel}>
                    Mevcut Şifre
                  </label>
                  <div className={styles.passwordInputWrapper}>
                    <input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Mevcut şifrenizi girin"
                      className={styles.formControl}
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="newPassword" className={styles.formLabel}>
                    Yeni Şifre
                  </label>
                  <div className={styles.passwordInputWrapper}>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Yeni şifrenizi girin"
                      className={styles.formControl}
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="confirmPassword" className={styles.formLabel}>
                    Yeni Şifre (Tekrar)
                  </label>
                  <div className={styles.passwordInputWrapper}>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Yeni şifrenizi tekrar girin"
                      className={styles.formControl}
                    />
                  </div>
                </div>
                
                {passwordError && (
                  <div className={styles.errorMessage}>
                    <FaExclamationTriangle className={styles.errorIcon} />
                    {passwordError}
                  </div>
                )}
              </div>
              
              <div className={styles.passwordModalFooter}>
                <button 
                  className={styles.modalCancelButton}
                  onClick={() => setShowPasswordModal(false)}
                >
                  İptal
                </button>
                <button 
                  className={styles.modalPrimaryButton}
                  onClick={handleChangePassword}
                >
                  <FaCheckCircle className={styles.buttonIcon} />
                  Şifreyi Değiştir
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Education Module Modal */}
        {selectedEducationSOP && (
          <EducationModule
            show={showEducationModule}
            onHide={() => setShowEducationModule(false)}
            sopData={selectedEducationSOP}
            onComplete={handleEducationComplete}
          />
        )}

        {/* Education Player Modal (for Indication Educations) */}
        {selectedIndicationEducation && (
          <EducationPlayer
            show={showEducationPlayer}
            onHide={() => {
              setShowEducationPlayer(false);
              setSelectedIndicationEducation(null);
            }}
            education={selectedIndicationEducation}
            onComplete={handleIndicationEducationComplete}
          />
        )}

        {/* Complaint Status Modal */}
        {showComplaintStatusModal && selectedComplaint && (
          <div className={styles.modalBackdrop}>
            <motion.div
              className={styles.complaintStatusModal}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className={styles.complaintStatusModalHeader}>
                <h3 className={styles.complaintStatusModalTitle}>
                  <FaExclamationTriangle className={styles.complaintStatusModalIcon} />
                  Şikayet Durumu Güncelle
                </h3>
                <button
                  className={styles.modalCloseButton}
                  onClick={() => {
                    setShowComplaintStatusModal(false);
                    setSelectedComplaint(null);
                  }}
                  aria-label="Close modal"
                >
                  <FaTimes />
                </button>
              </div>

              <div className={styles.complaintStatusModalBody}>
                <div className={styles.complaintInfoCard}>
                  <div className={styles.complaintInfoRow}>
                    <div className={styles.complaintInfoLabel}>
                      <FaFileAlt className={styles.complaintInfoIcon} />
                      Konu
                    </div>
                    <div className={styles.complaintInfoValue}>{selectedComplaint.subject}</div>
                  </div>

                  <div className={styles.complaintInfoRow}>
                    <div className={styles.complaintInfoLabel}>
                      <FaUser className={styles.complaintInfoIcon} />
                      Şikayet Eden
                    </div>
                    <div className={styles.complaintInfoValue}>
                      {selectedComplaint.employeeName}
                      <span className={styles.complaintEmail}>({selectedComplaint.employeeEmail})</span>
                    </div>
                  </div>

                  <div className={styles.complaintInfoRow}>
                    <div className={styles.complaintInfoLabel}>
                      <FaExclamationTriangle className={styles.complaintInfoIcon} />
                      Tür
                    </div>
                    <div className={styles.complaintInfoValue}>
                      <span className={styles.complaintTypeBadgeModal}>
                        {selectedComplaint.typeLabel || selectedComplaint.type}
                      </span>
                    </div>
                  </div>

                  <div className={styles.complaintInfoRow}>
                    <div className={styles.complaintInfoLabel}>
                      <FaClock className={styles.complaintInfoIcon} />
                      Mevcut Durum
                    </div>
                    <div className={styles.complaintInfoValue}>
                      <span className={`${styles.statusBadgeModal} ${
                        selectedComplaint.status === 'PENDING' ? styles.statusPendingModal :
                        selectedComplaint.status === 'INCELENIYOR' ? styles.statusInProgressModal :
                        selectedComplaint.status === 'RESOLVED' ? styles.statusApprovedModal :
                        styles.statusRejectedModal
                      }`}>
                        {selectedComplaint.status === 'PENDING' ? 'Beklemede' :
                         selectedComplaint.status === 'INCELENIYOR' ? 'İnceleniyor' :
                         selectedComplaint.status === 'RESOLVED' ? 'Çözüldü' : 'Kapatıldı'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.complaintDetailsSection}>
                  <div className={styles.complaintDetailsLabel}>Detay</div>
                  <div className={styles.complaintDetailsText}>{selectedComplaint.details}</div>
                </div>

                <div className={styles.statusUpdateSection}>
                  <div className={styles.statusUpdateLabel}>Yeni Durum Seçin</div>
                  <div className={styles.statusButtonsGrid}>
                    {complaintStatusOptions.map((option) => {
                      const isActive = selectedComplaint.status === option.value;
                      const getStatusClass = () => {
                        switch(option.value) {
                          case 'PENDING': return styles.statusBtnPending;
                          case 'INCELENIYOR': return styles.statusBtnInProgress;
                          case 'RESOLVED': return styles.statusBtnResolved;
                          case 'CLOSED': return styles.statusBtnClosed;
                          default: return '';
                        }
                      };
                      return (
                        <button
                          key={option.value}
                          className={`${styles.statusUpdateBtn} ${getStatusClass()} ${isActive ? styles.statusUpdateBtnActive : ''}`}
                          onClick={() => handleComplaintStatusUpdate(option.value)}
                          disabled={complaintStatusLoading || isActive}
                        >
                          {complaintStatusLoading && !isActive ? (
                            <FaSpinner className={styles.spinnerIcon} />
                          ) : (
                            <>
                              {option.value === 'PENDING' && <FaClock />}
                              {option.value === 'INCELENIYOR' && <FaEye />}
                              {option.value === 'RESOLVED' && <FaCheckCircle />}
                              {option.value === 'CLOSED' && <FaTimesCircle />}
                            </>
                          )}
                          <span>{option.label}</span>
                          {isActive && <span className={styles.currentBadge}>Mevcut</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Existing Replies Section */}
                {selectedComplaint.replies && selectedComplaint.replies.length > 0 && (
                  <div className={styles.repliesSection}>
                    <div className={styles.repliesSectionTitle}>
                      <FaComments className="me-2" />
                      Önceki Yanıtlar ({selectedComplaint.replies.length})
                    </div>
                    <div className={styles.repliesList}>
                      {selectedComplaint.replies.map((reply, index) => (
                        <div key={index} className={styles.replyItem}>
                          <div className={styles.replyHeader}>
                            <span className={styles.replyAuthor}>{reply.repliedBy}</span>
                            <span className={styles.replyDate}>
                              {reply.repliedAt?.toDate
                                ? formatDate(reply.repliedAt.toDate())
                                : formatDate(new Date(reply.repliedAt))}
                            </span>
                          </div>
                          <div className={styles.replyText}>{reply.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reply Input Section */}
                <div className={styles.replyInputSection}>
                  <div className={styles.replyInputLabel}>
                    <FaEdit className="me-2" />
                    Yanıt Yaz
                  </div>
                  <textarea
                    className={styles.replyTextarea}
                    placeholder="Dilekçeye yanıtınızı buraya yazınız..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={4}
                  />
                  <button
                    className={styles.replySubmitBtn}
                    onClick={handleReplySubmit}
                    disabled={replyLoading || !replyText.trim()}
                  >
                    {replyLoading ? (
                      <>
                        <FaSpinner className="spinner-border-sm me-2" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Yanıtı Gönder
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className={styles.complaintStatusModalFooter}>
                <button
                  className={styles.modalCancelButton}
                  onClick={() => {
                    setShowComplaintStatusModal(false);
                    setSelectedComplaint(null);
                  }}
                >
                  Kapat
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </Container>
    </div>

      {/* Address Edit Modal */}
      <Modal show={showAddressModal} onHide={() => setShowAddressModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHome className="me-2" />
            Adres Bilgilerini Düzenle
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Ev Adresi</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={editingAddress}
              onChange={(e) => setEditingAddress(e.target.value)}
              placeholder="Ev adresinizi giriniz..."
              disabled={addressLoading}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>İş Adresi</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={editingWorkAddress}
              onChange={(e) => setEditingWorkAddress(e.target.value)}
              placeholder="İş adresinizi giriniz..."
              disabled={addressLoading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowAddressModal(false)}
            disabled={addressLoading}
          >
            İptal
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddressUpdate}
            disabled={addressLoading || !editingAddress.trim()}
          >
            {addressLoading ? (
              <>
                <FaSpinner className="spinner-border-sm me-2" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                Kaydet
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Phone Edit Modal - Only Emergency Phone */}
      <Modal show={showPhoneModal} onHide={() => setShowPhoneModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaPhone className="me-2" />
            Acil Durum Telefonu Düzenle
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Acil Durum Telefon Numarası</Form.Label>
            <Form.Control
              type="tel"
              value={editingEmergencyPhone}
              onChange={(e) => setEditingEmergencyPhone(e.target.value)}
              placeholder="Acil durum için telefon numarası giriniz..."
              disabled={phoneLoading}
              autoFocus
            />
            <Form.Text className="text-muted">
              Acil durumlarda ulaşılabilecek alternatif numara
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowPhoneModal(false)}
            disabled={phoneLoading}
          >
            İptal
          </Button>
          <Button 
            variant="primary" 
            onClick={handlePhoneUpdate}
            disabled={phoneLoading || !editingEmergencyPhone.trim()}
          >
            {phoneLoading ? (
              <>
                <FaSpinner className="spinner-border-sm me-2" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                Kaydet
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CV Management Modal */}
      <CVManagementModal 
        show={showCVModal}
        onHide={() => setShowCVModal(false)}
        employeeId={employeeId}
        userData={userData}
      />
    </>
  );
};

export default ProfilePage;
